import type {
  BillsDue,
  NextCheckNumber,
  PrintUnprintedChecksResult,
  RegisterCheck,
  SetPrintToCheckResult,
  SetRegisterQuantityRequest,
  SetRegisterQuantityResult,
  UnprintedChecks,
  VoidRegisterCheckResult,
  CompleteReminderRequest,
  CreateEquipmentRequest,
  CreateReminderRequest,
  DueSoonReminders,
  Equipment,
  HomeLayout,
  HoursReading,
  LogHoursRequest,
  Reminder,
  UpdateEquipmentRequest,
  UpdateReminderRequest,
  CreatePieceTicketRequest,
  CreatePieceTypeRequest,
  PieceRunSummary,
  PieceTicket,
  PieceTicketQuery,
  PieceType,
  UpdatePieceTicketRequest,
  UpdatePieceTypeRequest,
  AddAnimalWeightRequest,
  Animal,
  AnimalWeight,
  BulkCreateAnimalsRequest,
  BulkCreateAnimalsResponse,
  CalfCropRequest,
  CalfCropResponse,
  LivestockHeadcount,
  AnimalGroup,
  AnimalStatus,
  AnimalStatusChange,
  BalanceSheetReport,
  BooksActivityEntry,
  BankStatementLine,
  ImportStatementResult,
  SettleDocumentRequest,
  SettleDocumentResponse,
  UnmatchedStatementLine,
  ReconcileCandidate,
  MatchStatementLineRequest,
  MatchStatementLineResult,
  UnmatchStatementLineRequest,
  ReconcileSummary,
  // ef-058 anchors — see the `ef-058` block in `demoApi` below.
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  ChangeEmployeeRateRequest,
  ChangeEmployeeRateResponse,
  EmployeeRateRow,
  BankImportMapping,
  BankImportMappingRequest,
  AutoMatchResult,
  AddStatementEntryRequest,
  AddStatementEntryResult,
  ScheduleFReport,
  Vendor1099Report,
  VendorTax,
  VendorTaxList,
  EntityTax,
  EntityTaxList,
  PutEntityTaxRequest,
  PutVendorTaxRequest,
  PutVendorTaxIdRequest,
  ProductionSalesReport,
  VendorPurchasesReport,
  LivestockInventoryReport,
  CropProductionReport,
  CashFlowReport,
  AgingReport,
  // ef-053-0a: five stub-type imports for the five report slices pre-registered as
  // scaffolding (D1, `ef-053-dispatch.md`) — see this file's own `not built` stub
  // methods below.
  IncomeStatementReport,
  GrainInventoryReport,
  MilkProductionReport,
  FieldApplicationReport,
  PartnerSplitReport,
  // ef-054-0a: five more stub-type imports for the five report slices pre-registered as
  // scaffolding (D1, `ef-054-dispatch.md`) — see this file's own `not built` stub
  // methods below.
  PartnerSplitDollarReport,
  GlDetailPage,
  TrialBalanceReport,
  CashReceiptsPage,
  PayrollTaxLiabilityReport,
  // ef-055-0a: two more stub-type imports for the two report slices pre-registered as
  // scaffolding (D1, `ef-055-dispatch.md`) — see this file's own `not built` stub
  // methods below.
  ReconciliationReport,
  CropPlantingReport,
  PayrollRegisterReport,
  CloseYearResult,
  YearLockStatus,
  ChangeAnimalStatusRequest,
  CreateDocumentRequest,
  Document,
  DocumentAging,
  VendorPaymentHistoryRow,
  TenantUser,
  Account,
  AccountWriteResult,
  CreateAccountRequest,
  UpdateAccountRequest,
  AccountImportResult,
  BusinessEntity,
  CreateEntityRequest,
  UpdateEntityRequest,
  CreateAnimalRequest,
  CreateEntryRequest,
  CreateEntryResponse,
  CreatePurchaseRequest,
  CreateSaleRequest,
  Entry,
  Guid,
  Herd,
  LivestockPostingResponse,
  PaidLeaveRequest,
  PaidLeaveResponse,
  PaycheckRequest,
  PaycheckResponse,
  PaymentMethod,
  PayrollEmployee,
  PayrollRunRequest,
  ApplyHoursRequest,
  ApplyHoursResult,
  HoursReport,
  LabourReport,
  StatePaidLeavePrograms,
  TimesheetExport,
  ManualTimeRequest,
  MyTime,
  PayPeriod,
  SignTimesheetRequest,
  PunchInResult,
  PunchOutResult,
  PunchRequest,
  PostRegisterEntryRequest,
  RegisterEntry,
  ReviewTimeRequest,
  Shift,
  SubmitTimeOffRequest,
  TimeEntry,
  TimeOffRequest,
  YearEndForms,
  YearEndRequest,
  QuarterlyForm,
  QuarterlyRequest,
  AnnualFutaForm,
  AnnualFutaRequest,
  Allocation,
  CreatePlaceRequest,
  CreateJobCodeRequest,
  CreateShiftRequest,
  FilingCalendar,
  GetPayRunsResponse,
  JobCode,
  Place,
  PutAllocationsRequest,
  PayrollRunResponse,
  PayRunFinalizeResult,
  PayRunPostResult,
  PayRunPrintResult,
  VoidCheckRequest,
  VoidCheckResult,
  ProfitAndLossReport,
  ReviewTimeOffRequest,
  TimeOffStatus,
  UpdateShiftRequest,
  VoidPayRunRequest,
  VoidPayRunResult,
  ReceiptAnalysis,
  SessionUser,
  UpdateAnimalRequest,
  UpdateEmployeePayRequest,
  UpdateEmployeeW4Request,
  UpdateJobCodeRequest,
  UpdatePlaceRequest,
  UsageCounts,
  ValidationProblem,
  Vendor,
  CreateVendorRequest,
  UpdateVendorRequest,
  RecurringTemplate,
  CreateRecurringRequest,
  UpdateRecurringRequest,
  PostRecurringNowResponse,
  Field,
  CreateFieldRequest,
  UpdateFieldRequest,
  FieldCrop,
  CreateFieldCropRequest,
  UpdateFieldCropRequest,
  FieldCropApplication,
  CreateFieldApplicationRequest,
  FieldCropHarvest,
  CreateFieldHarvestRequest,
  FarmOverview,
  GrainBin,
  CreateGrainBinRequest,
  CreateGrainStorageFeeRequest,
  GrainStorageFee,
  CreateGrainLoadRequest,
  GrainLoad,
  GrainLoadImportResult,
  GrainLoadImportFormat,
  GrainLoadImportRefusal,
  GrainLoadRowError,
  ScaleAlias,
  ScaleAliasKind,
  ScaleAliasUnmapped,
  CreateScaleAliasRequest,
  ScaleAliasBatchRequest,
  ScaleAliasBatchResponse,
  Commodity,
  GrainContract,
  CreateGrainContractRequest,
  UpdateGrainContractRequest,
  GrainDelivery,
  CreateGrainDeliveryRequest,
  MilkTicket,
  CreateMilkTicketRequest,
  PartnerSplit,
  CreatePartnerSplitRequest,
  UpdatePartnerSplitRequest,
  Memo,
  CreateMemoRequest,
} from "./types";

/**
 * Typed fetch wrapper for the demo API.
 *
 * Every request carries `X-Demo-Tenant` so the API/PowerSync layer can
 * route between the two booth tenants (Booth Demo vs. Live Wall) per
 * docs/demo/tradeshow-2026-09.md. There is deliberately no fallback value —
 * a missing tenant should fail loudly in dev rather than silently write to
 * (or read from) the wrong tenant.
 */

const TENANT_HEADER = "X-Demo-Tenant";

/**
 * How long a visitor waits for their own entry before being told something is wrong.
 *
 * Scoped to the submit, not applied to `request` generally: the dashboard's polling GETs are
 * short-lived and harmless, and the live feed is an EventSource that must stay open for
 * minutes. A blanket timeout here would cut the wall's own stream.
 */
const SUBMIT_TIMEOUT_MS = 15_000;

/*
  The receipt analyze bound. Deliberately much longer than SUBMIT_TIMEOUT_MS, and the
  number is derived from the server rather than picked.

  SparkReceiptAnalyzer takes one try and one retry (`MaxAttempts`), each with a fresh
  8s per-attempt timeout (`ReceiptAnalyzerRegistration.DefaultTimeoutSeconds`), and
  then degrades to "no suggestion" — a 200 the visitor can still act on. A client bound
  below that 16s worst case would abort a request the server was about to answer WELL,
  turning graceful degradation into an error message. 30s clears it with headroom and
  still bounds a genuinely hung proxy, which is the case this exists for.

  If the Spark timeout is ever raised (Q-SPARK), raise this with it.
*/
const ANALYZE_TIMEOUT_MS = 30_000;

/**
 * ef-017 — same bound as `SUBMIT_TIMEOUT_MS`, for a punch rather than an entry.
 *
 * Neither `punchIn` nor `punchOut` carried a client-side timeout before this: a request
 * that hung (the api container PAUSED, not stopped) would leave "One moment…" on screen
 * forever, with no error and — unlike `neverReachedTheServer` — no signal that would ever
 * let the offline outbox queue it either. offline-punch.spec.ts's own AC5 pins the
 * OUTCOME ("a hung punch-in is not queued, and ends in an actionable message") without
 * naming a number; 15s matches the entry path's own budget and comfortably clears the
 * test's 20s window for that message to appear. Applies to a REPLAY too (the outbox's
 * `punchOutbox.ts` calls the same two functions) — there, a timeout is treated like any
 * other ambiguous failure: held and retried, never dropped, because the replay is
 * idempotent server-side (`clientPunchId`) and a duplicate is not the risk a retry runs.
 */
const PUNCH_TIMEOUT_MS = 15_000;

/**
 * The `status` an ApiError carries when WE gave up, not when the server answered.
 *
 * Deliberately not 0 and not a real HTTP status: 0 already means "the fetch never left the
 * device", which the outbox treats as safe to replay, and a timeout is the one case where
 * that is NOT safe — the request may have arrived and been recorded. -1 keeps it out of
 * both families and makes the ambiguity explicit at every call site.
 */
export const REQUEST_TIMED_OUT = -1;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * True when the request demonstrably never reached the application.
   *
   * `status === 0` is a fetch that never completed — no network. 502 and 504 are the
   * REVERSE PROXY saying it could not reach or could not get a reply from the API: Caddy
   * answered, the application never did. For a submitted entry the information state is
   * identical to being offline — nothing was recorded — so the outbox can hold it safely.
   *
   * 500 and 503 are deliberately NOT here. A 500 means the application ran and failed; a
   * 503 can come from the app itself (a health gate, a limiter). Those mean "the server
   * spoke", and retrying them blindly is how you get a duplicate.
   *
   * Found on 2026-09-02 by stopping the api container and submitting an entry as a
   * visitor: `navigator.onLine` stays TRUE because the phone's network is fine, no offline
   * event fires, and the visitor was shown a raw "502 Bad Gateway" in a live region. The
   * offline suite's own of-002 is titled "an entry created offline is accepted and marked
   * pending, NEVER an error" — this is the case that slipped past it, because every
   * automated offline test simulates a dead NETWORK and the booth's likelier failure is a
   * live hotspot with an unreachable back end.
   */
  get neverReachedTheServer(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 504;
  }
}

/**
 * Exported (as well as used internally) because `offline/referenceCache.ts` keys its
 * snapshot by tenant, and re-reading the env var there would be a second source of
 * truth for the same value — the kind that stays correct right up until one of them
 * moves.
 */
export function demoTenantId(): string {
  return getTenant();
}

function getTenant(): string {
  const tenant = import.meta.env.VITE_DEMO_TENANT;
  if (!tenant) {
    throw new Error(
      "VITE_DEMO_TENANT is not set. Refusing to call the demo API without a tenant — " +
        "see web/.env.example.",
    );
  }
  return tenant;
}

function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? "";
}

/**
 * URL for the Server-Sent Events live feed — `GET /api/demo/entries/stream`.
 *
 * feature/auth: this route is now session-authenticated (see DemoEndpoints.cs),
 * not header- or query-string-driven. No tenant travels on this URL any more —
 * `EventSource` cannot set custom headers, but it DOES send same-origin cookies
 * automatically with no extra wiring, so the session cookie reaches this route
 * exactly like every other authenticated fetch, and the old `?tenant=` fallback
 * this function used to append has nothing left to do.
 */
export function entriesStreamUrl(): string {
  return `${baseUrl()}/api/demo/entries/stream`;
}

/**
 * The URL a plain `<a href>` or `<img src>` should use for an API path. ef-006 — the
 * receipt image is fetched by the BROWSER rather than by `request()`, so it needs the same
 * base resolution the fetch path uses. Same origin means the session cookie rides along
 * exactly as it does for every other authenticated route.
 */
export function apiUrl(path: string): string {
  return `${baseUrl()}${path}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${baseUrl()}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      // Same-origin is the fetch default in every modern browser, but this is
      // spelled out because it matters here: the session cookie (feature/auth)
      // must ride along on every call for the routes that now require it, and
      // `credentials: "omit"` would silently turn a real login into "always 401".
      credentials: "same-origin",
      headers: {
        // FormData must NOT get an explicit Content-Type. The browser sets
        // multipart/form-data itself and appends the boundary token; overriding it
        // produces a header with no boundary and the server cannot parse the body.
        ...(init?.body && !(init.body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        [TENANT_HEADER]: getTenant(),
        ...init?.headers,
      },
    });
  } catch (cause) {
    // Network failure (offline, DNS, CORS preflight rejection, etc.) — the
    // caller decides how to surface this (empty/error state), never silent.
    //
    // A TIMEOUT IS NOT A NETWORK FAILURE, and must not be flattened into one. Both reject
    // the fetch, but they carry opposite information: an offline fetch never left the
    // device, while a timeout means we gave up on a request that may well have arrived and
    // been recorded. Status 0 means "never reached the server" and drives the outbox
    // (ApiError.neverReachedTheServer) — sending a timeout down that path would silently
    // replay an entry the server might already hold, and nothing dedupes it, because
    // clientId is a local queue key that is never sent.
    if (cause instanceof DOMException && cause.name === "TimeoutError") {
      throw new ApiError("The request took too long to answer.", REQUEST_TIMED_OUT, url);
    }

    throw new ApiError(
      cause instanceof Error ? cause.message : "Network request failed",
      0,
      url,
    );
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
      response.status,
      url,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Result of asking for the payment-method pick-list. Modeled as a
 * discriminated union rather than "throw or return []" because a 404 here
 * is an *expected, graceful* state (the route may not be deployed to this
 * environment yet — see the task note on api/client.ts `getPaymentMethods`)
 * distinct from a genuine failure (network down, 500). Callers use the
 * `status` tag to decide whether to hide the payment-method step or show
 * an error, and never see an exception for the 404 case specifically.
 */
export type PaymentMethodsResult =
  | { status: "ok"; methods: PaymentMethod[] }
  | { status: "unavailable" };

/**
 * The bookkeeper's labour-by-job-code export — GET /api/demo/reports/labour/export.
 * ef-044-5.
 *
 * Same shape and same reasoning as `TimesheetExport`: `delimitedFile` is the server
 * writer's bytes verbatim, and a caller turns it into a Blob and adds a filename — never
 * re-serialises from rows, which would be a second implementation of the file format.
 * `rowCount` equals the on-screen report's own `lines.length` for the identical window.
 */
export interface LabourReportExport {
  start: string;
  end: string;
  rowCount: number;
  delimitedFile: string;
  delimitedFileName: string;
}

/**
 * One projected cash movement from `GET /api/demo/home/cash-flow` — ef-048-5. `date` is
 * the raw `yyyy-MM-dd` string the API returns; the Home card renders it as-is rather than
 * reformatting client-side, per the build brief.
 */
export interface CashFlowEvent {
  date: string;
  kind: "payroll" | "bill" | "invoice";
  label: string;
  amount: number;
  runningCash: number;
}

/** Wire shape for `GET /api/demo/home/cash-flow` — the Home dashboard's read-only
 *  cash-flow projection (ef-048-5, `CashFlowPlannerEndpoints.cs`). */
export interface CashFlowPlanner {
  asOf: string;
  weeks: number;
  currentCash: number;
  events: CashFlowEvent[];
}

export const demoApi = {
  /** GET /api/demo/entities — what removed the VITE_DEMO_ENTITY_ID hardcode. */
  getEntities: (signal?: AbortSignal): Promise<BusinessEntity[]> =>
    request<BusinessEntity[]>("/api/demo/entities", { signal }),

  getVendors: (signal?: AbortSignal): Promise<Vendor[]> =>
    request<Vendor[]>("/api/demo/vendors", { signal }),

  /** POST /api/demo/vendors (ef-042-3). A 409 means a vendor with that name already
   *  exists (VendorEndpoints.cs). */
  createVendor: (body: CreateVendorRequest, signal?: AbortSignal): Promise<Vendor> =>
    request<Vendor>("/api/demo/vendors", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/vendors/{id} (ef-042-3) — a partial update; an omitted `name` keeps
   *  its current value. Same 409 as create when the new name collides with another vendor. */
  updateVendor: (id: Guid, body: UpdateVendorRequest, signal?: AbortSignal): Promise<Vendor> =>
    request<Vendor>(`/api/demo/vendors/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/vendors/{id} (ef-042-3) — a real delete, unlike `archivePlace`: a
   *  vendor has no archived state. A 409 means at least one entry still references it. */
  deleteVendor: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/vendors/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  // ---- ef-064-22: the payer's tax identity (owner only) -------------------------------------------------------------------------

  /** GET /api/demo/entities/tax - every entity with its tax status and MASKED tax ID, in ONE call. */
  getEntityTaxList: (signal?: AbortSignal): Promise<EntityTaxList> =>
    request<EntityTaxList>("/api/demo/entities/tax", { signal }),

  /** PUT /api/demo/entities/{id}/tax - the mailing address, replaced as a whole; never touches the tax ID. */
  putEntityTax: (id: Guid, body: PutEntityTaxRequest, signal?: AbortSignal): Promise<EntityTax> =>
    request<EntityTax>(`/api/demo/entities/${encodeURIComponent(id)}/tax`, { method: "PUT", body: JSON.stringify(body), signal }),

  /** PUT /api/demo/entities/{id}/tax-id - the number lives in this body and nowhere else. Answers the MASK only. */
  putEntityTaxId: (id: Guid, body: PutVendorTaxIdRequest, signal?: AbortSignal): Promise<EntityTax> =>
    request<EntityTax>(`/api/demo/entities/${encodeURIComponent(id)}/tax-id`, { method: "PUT", body: JSON.stringify(body), signal }),

  /** DELETE /api/demo/entities/{id}/tax-id */
  deleteEntityTaxId: (id: Guid, signal?: AbortSignal): Promise<EntityTax> =>
    request<EntityTax>(`/api/demo/entities/${encodeURIComponent(id)}/tax-id`, { method: "DELETE", signal }),

  // ---- ef-064-17 / 64-18 anchors: a payee's tax identity (owner only) ----------------------------------------------------------

  /** GET /api/demo/vendors/tax - every vendor with its tax status and MASKED tax ID, in ONE call (the vendors page reads it once, not once per row). */
  getVendorTaxList: (signal?: AbortSignal): Promise<VendorTaxList> =>
    request<VendorTaxList>("/api/demo/vendors/tax", { signal }),

  /** GET /api/demo/vendors/{id}/tax. 404 when the vendor does not resolve on this tenant. */
  getVendorTax: (id: Guid, signal?: AbortSignal): Promise<VendorTax> =>
    request<VendorTax>(`/api/demo/vendors/${encodeURIComponent(id)}/tax`, { signal }),

  /** PUT /api/demo/vendors/{id}/tax - the W-9 profile, replaced as a whole; never touches the tax ID. 400 names the field. */
  putVendorTax: (id: Guid, body: PutVendorTaxRequest, signal?: AbortSignal): Promise<VendorTax> =>
    request<VendorTax>(`/api/demo/vendors/${encodeURIComponent(id)}/tax`, {
      method: "PUT",
      body: JSON.stringify(body),
      signal,
    }),

  /** PUT /api/demo/vendors/{id}/tax-id - sets or replaces the tax ID; answers the MASK only. The number lives in this body and nowhere else:
   *  never in a URL, a log line, a stored draft or a retry queue. 400 names `tin` / `tinType` and never echoes the value. */
  putVendorTaxId: (id: Guid, body: PutVendorTaxIdRequest, signal?: AbortSignal): Promise<VendorTax> =>
    request<VendorTax>(`/api/demo/vendors/${encodeURIComponent(id)}/tax-id`, {
      method: "PUT",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/vendors/{id}/tax-id - removes it (removing nothing is a 200 no-op). */
  deleteVendorTaxId: (id: Guid, signal?: AbortSignal): Promise<VendorTax> =>
    request<VendorTax>(`/api/demo/vendors/${encodeURIComponent(id)}/tax-id`, {
      method: "DELETE",
      signal,
    }),

  /**
   * GET /api/demo/employees — the payroll roster, tenant-scoped by RLS.
   *
   * This is the call that replaced `PAYROLL_EMPLOYEES`, the twelve-element array the
   * Payroll and MN Paid Leave screens used to import. Unlike `getPaymentMethods` below,
   * a 404 here is NOT degraded to a graceful empty state: the payroll screens have
   * nothing to show and nothing to compute without a roster, and an empty roster
   * rendered as if it were an answer is exactly the fixture-shaped dishonesty this
   * whole change removes. It throws like every other endpoint, and the screen says the
   * roster could not be loaded.
   */
  getEmployees: (signal?: AbortSignal): Promise<PayrollEmployee[]> =>
    request<PayrollEmployee[]>("/api/demo/employees", { signal }),

  getAccounts: (signal?: AbortSignal): Promise<Account[]> =>
    request<Account[]>("/api/demo/accounts", { signal }),

  /**
   * GET /api/demo/vendor-payment-history — beat 3's history from the ledger. See
   * VendorPaymentHistoryRow.
   */
  getVendorPaymentHistory: (signal?: AbortSignal): Promise<VendorPaymentHistoryRow[]> =>
    request<VendorPaymentHistoryRow[]>("/api/demo/vendor-payment-history", { signal }),

  /**
   * GET /api/demo/payment-methods. This route may not exist yet on every
   * environment this client is pointed at — a 404 is treated as "not
   * deployed here" and degrades to `{ status: "unavailable" }` rather than
   * throwing, so a stale/partial backend deploy can never crash the
   * quick-entry flow. Any other failure (network, 5xx, a malformed tenant
   * header) still throws ApiError like every other endpoint here — that is
   * a real failure, not a graceful "not built yet" case.
   */
  getPaymentMethods: async (signal?: AbortSignal): Promise<PaymentMethodsResult> => {
    try {
      const methods = await request<PaymentMethod[]>("/api/demo/payment-methods", { signal });
      return { status: "ok", methods };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return { status: "unavailable" };
      }
      throw err;
    }
  },

  /**
   * GET /api/demo/entries — the laptop dashboard feed. feature/auth: this route is
   * now session-authenticated; the tenant header this helper still sends is ignored
   * server-side. A 401 here means "not logged in", surfaced by useEntries.ts the same
   * way any other ApiError is (see useSession.ts for the app-level login gate that
   * keeps this route from ever being called before a session exists).
   */
  getEntries: (signal?: AbortSignal): Promise<Entry[]> =>
    request<Entry[]>("/api/demo/entries", { signal }),

  /**
   * GET /api/demo/books/activity — Books' Activity tab, ef-042-0a. The real, uncurated
   * ledger feed: every entry regardless of account kind, unlike `getEntries` above.
   * Owner-only, same session-authenticated discipline as every other business-record
   * route (a 401 means "not logged in", a 403 means "logged in as an employee").
   */
  getBooksActivity: (
    params: { source?: string; accountId?: Guid; from?: string; to?: string; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<BooksActivityEntry[]> => {
    const qs = new URLSearchParams();
    if (params.source) qs.set("source", params.source);
    if (params.accountId) qs.set("accountId", params.accountId);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<BooksActivityEntry[]>(`/api/demo/books/activity${query ? `?${query}` : ""}`, { signal });
  },

  /**
   * GET /api/demo/books/documents — ef-043-4, invoices (AR) and bills (AP) over the
   * `document` table. Owner-only, same session-authenticated discipline as
   * `getBooksActivity` above. `outstanding` filters to documents whose derived balance is
   * (`true`) or is not (`false`) greater than zero; omit it for every document regardless.
   */
  getDocuments: (
    params: {
      kind?: "invoice" | "bill";
      outstanding?: boolean;
      accountId?: Guid;
      counterpartyId?: Guid;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<Document[]> => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set("kind", params.kind);
    if (params.outstanding != null) qs.set("outstanding", String(params.outstanding));
    if (params.accountId) qs.set("accountId", params.accountId);
    if (params.counterpartyId) qs.set("counterpartyId", params.counterpartyId);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<Document[]>(`/api/demo/books/documents${query ? `?${query}` : ""}`, { signal });
  },

  /**
   * GET /api/demo/books/documents/aging — ef-049-0e, the three sums the vendors screen's
   * own aging strip reads. Server-side, unbounded by the 200-row cap `getDocuments`'s own
   * `limit` still applies to the document list rendered below the strip.
   */
  getDocumentsAging: (signal?: AbortSignal): Promise<DocumentAging> =>
    request<DocumentAging>("/api/demo/books/documents/aging", { signal }),

  /**
   * GET /api/demo/books/reconcile/{accountId}/unmatched — ef-043-2, statement lines on
   * this account with no matched entry_line yet. Owner-only, same session-authenticated
   * discipline as `getBooksActivity` above.
   */
  getReconcileUnmatched: (
    accountId: Guid,
    limit?: number,
    signal?: AbortSignal,
  ): Promise<UnmatchedStatementLine[]> => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set("limit", String(limit));
    const query = qs.toString();
    return request<UnmatchedStatementLine[]>(
      `/api/demo/books/reconcile/${accountId}/unmatched${query ? `?${query}` : ""}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/books/reconcile/{accountId}/candidates — ef-043-2, unreconciled entry
   * lines eligible to be matched against `statementLineId`. Owner-only, same
   * session-authenticated discipline as `getBooksActivity` above.
   */
  getReconcileCandidates: (
    accountId: Guid,
    statementLineId: Guid,
    signal?: AbortSignal,
  ): Promise<ReconcileCandidate[]> => {
    const qs = new URLSearchParams();
    qs.set("statementLineId", statementLineId);
    return request<ReconcileCandidate[]>(
      `/api/demo/books/reconcile/${accountId}/candidates?${qs.toString()}`,
      { signal },
    );
  },

  /**
   * POST /api/demo/books/reconcile/{accountId}/match — ef-043-2, matches one statement
   * line to one entry line and sets both `bank_statement_line.matchedEntryLineId` and
   * `entry_line.reconciledOn` together. Owner-only, same session-authenticated discipline
   * as `getBooksActivity` above.
   */
  matchStatementLine: (
    accountId: Guid,
    body: MatchStatementLineRequest,
    signal?: AbortSignal,
  ): Promise<MatchStatementLineResult> =>
    request<MatchStatementLineResult>(`/api/demo/books/reconcile/${accountId}/match`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * POST /api/demo/books/reconcile/{accountId}/unmatch — ef-043-2, clears a match. A 409
   * means the matched entry falls in a locked period and cannot be unmatched — an
   * application-level guard, not the ef-043-7 DB trigger (which deliberately leaves
   * `reconciledOn` unguarded). Owner-only, same session-authenticated discipline as
   * `getBooksActivity` above.
   */
  unmatchStatementLine: (
    accountId: Guid,
    body: UnmatchStatementLineRequest,
    signal?: AbortSignal,
  ): Promise<void> =>
    request<void>(`/api/demo/books/reconcile/${accountId}/unmatch`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * GET /api/demo/books/reconcile/{accountId}/summary — ef-043-3, the one new read route
   * this slice adds. `statementEndingBalance` is the figure off the bank statement the
   * owner is checking against; the response echoes it back alongside the server's own
   * cleared-balance sum and the derived difference (0 means balanced). Owner-only, same
   * session-authenticated discipline as `getBooksActivity` above.
   */
  getReconcileSummary: (
    accountId: Guid,
    statementEndingBalance: number,
    signal?: AbortSignal,
  ): Promise<ReconcileSummary> => {
    const qs = new URLSearchParams();
    qs.set("statementEndingBalance", String(statementEndingBalance));
    return request<ReconcileSummary>(
      `/api/demo/books/reconcile/${accountId}/summary?${qs.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/schedule-f — ef-044-2, the Schedule F worksheet for one entity
   * and one tax year. Owner-only. A 404 means `entityId` does not resolve on this tenant.
   */
  getScheduleFReport: (
    entityId: Guid,
    taxYear: number,
    signal?: AbortSignal,
  ): Promise<ScheduleFReport> =>
    request<ScheduleFReport>(
      `/api/demo/reports/schedule-f?entityId=${encodeURIComponent(entityId)}&taxYear=${taxYear}`,
      { signal },
    ),

  /**
   * GET /api/demo/reports/1099-nec — ef-047-5, the 1099-NEC worksheet for one entity and
   * one tax year. Owner-only. A 404 means `entityId` does not resolve on this tenant.
   */
  getVendor1099Report: (
    entityId: Guid,
    taxYear: number,
    signal?: AbortSignal,
  ): Promise<Vendor1099Report> =>
    request<Vendor1099Report>(
      `/api/demo/reports/1099-nec?entityId=${encodeURIComponent(entityId)}&taxYear=${taxYear}`,
      { signal },
    ),

  /**
   * GET /api/demo/reports/1099-nec/export — ef-047-5, the worksheet as a downloadable
   * file. RAW `text/csv`, not JSON-wrapped like `exportLabourReport`'s own response — this
   * route's own row-count-parity test reads the body directly with `.text()` — so this
   * bypasses `request<T>` (which always parses JSON) rather than forcing that shape here.
   */
  getVendor1099Csv: async (
    entityId: Guid,
    taxYear: number,
    signal?: AbortSignal,
  ): Promise<string> => {
    const url =
      `${baseUrl()}/api/demo/reports/1099-nec/export` +
      `?entityId=${encodeURIComponent(entityId)}&taxYear=${taxYear}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/production-sales — ef-050-3, qty/amount per (kind, unit) across
   * this entity's Harvest/Grain/Milk/Livestock activity for a caller-chosen window.
   * `entityId` is optional — omitted, the server resolves the tenant's own single/oldest
   * entity (`ProductionSalesReportEndpoints`' own remarks), which is what every fixture
   * this booth demo models. A 404 means `entityId` (if given) does not resolve on this
   * tenant.
   */
  getProductionSalesReport: (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<ProductionSalesReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    return request<ProductionSalesReport>(
      `/api/demo/reports/production-sales?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/production-sales/export — ef-050-3, the worksheet as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped like `exportLabourReport`'s own
   * response — this route's own row-count-parity test reads the body directly — so this
   * bypasses `request<T>` (which always parses JSON), same reasoning `getVendor1099Csv`
   * above gives.
   */
  getProductionSalesCsv: async (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    const url = `${baseUrl()}/api/demo/reports/production-sales/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/cash-flow — ef-052-1, inflow/outflow/net per calendar month
   * for this entity's already-posted activity, over a caller-chosen window. `entityId`
   * is optional — omitted, the server resolves the tenant's own single/oldest entity
   * (`CashFlowReportEndpoints`' own remarks), same defaulting `getProductionSalesReport`
   * above already takes. A 404 means `entityId` (if given) does not resolve on this
   * tenant.
   */
  getCashFlowReport: (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<CashFlowReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    return request<CashFlowReport>(`/api/demo/reports/cash-flow?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/cash-flow/export — ef-052-1, the by-month table as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getProductionSalesCsv` above gives.
   */
  getCashFlowCsv: async (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    const url = `${baseUrl()}/api/demo/reports/cash-flow/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/aging — ef-052-2, the AR/AP aging detail worksheet: one row per
   * outstanding document, bucketed by days overdue as of `asOf`, for one entity. `asOf`
   * defaults server-side to today when omitted; `entityId` is optional, same "resolve the
   * tenant's own single/oldest entity" defaulting as `getProductionSalesReport` above. A
   * 404 means `entityId` (if given) does not resolve on this tenant.
   */
  getAgingReport: (
    params: { asOf?: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<AgingReport> => {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    if (params.entityId) query.set("entityId", params.entityId);
    return request<AgingReport>(`/api/demo/reports/aging?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/aging/export — ef-052-2, the worksheet as a downloadable file.
   * RAW `text/csv`, not JSON-wrapped — this route's own row-count-parity test reads the
   * body directly — so this bypasses `request<T>` (which always parses JSON), same
   * reasoning `getProductionSalesCsv` above gives.
   */
  getAgingCsv: async (
    params: { asOf?: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    if (params.entityId) query.set("entityId", params.entityId);
    const url = `${baseUrl()}/api/demo/reports/aging/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/crop-production — ef-052-4, qty/amount per (field, crop, unit)
   * across this entity's `field_crop_harvest` activity for a caller-chosen window.
   * `entityId` is optional — omitted, the server resolves the tenant's own single/oldest
   * entity (`CropProductionReportEndpoints`' own remarks). A 404 means `entityId` (if
   * given) does not resolve on this tenant.
   */
  getCropProductionReport: (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<CropProductionReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    return request<CropProductionReport>(
      `/api/demo/reports/crop-production?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/crop-production/export — ef-052-4, the worksheet as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getProductionSalesCsv` above gives.
   */
  getCropProductionCsv: async (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    const url = `${baseUrl()}/api/demo/reports/crop-production/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/livestock-inventory — ef-052-5, the CURRENT-snapshot head count
   * per (herd, status). D4 — no `from`/`to`: unlike `getProductionSalesReport` above, this
   * report has no window to choose, so this call takes none. `entityId` is optional — same
   * "omitted resolves to the tenant's own single/oldest entity" reasoning
   * `getProductionSalesReport`'s own remarks give. A 404 means `entityId` (if given) does
   * not resolve on this tenant.
   */
  getLivestockInventoryReport: (
    params?: { entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<LivestockInventoryReport> => {
    const query = new URLSearchParams();
    if (params?.entityId) query.set("entityId", params.entityId);
    const qs = query.toString();
    return request<LivestockInventoryReport>(
      `/api/demo/reports/livestock-inventory${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/livestock-inventory/export — ef-052-5, the snapshot as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — same `request<T>`-bypass
   * reasoning `getProductionSalesCsv` above gives.
   */
  getLivestockInventoryCsv: async (
    params?: { entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams();
    if (params?.entityId) query.set("entityId", params.entityId);
    const qs = query.toString();
    const url = `${baseUrl()}/api/demo/reports/livestock-inventory/export${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  // ---- ef-053-0a: five report stubs, pre-registered as scaffolding (D1,
  // `ef-053-dispatch.md`) ------------------------------------------------------
  //
  // Each pair below is dead-until-wired: it exists only so `App.tsx`'s route branch and
  // `web/src/lib/reportRegistry.ts`'s own entry compile today, without either shared
  // file changing again once that slice's builder lands. Every method throws — no
  // route exists yet (the matching `MapXxx()` stub endpoint answers 501) — and each
  // slice's own builder REPLACES its pair with the real implementation. See
  // `ef-053-N-build.md` for N in 1..5.

  /**
   * GET /api/demo/reports/income-statement — ef-053-1, profit & loss for ONE entity, cash
   * or accrual. `entityId` is REQUIRED on this route (D2) — unlike every other report
   * above, there is no farm-wide/oldest-entity fallback to resolve to. A 404 means
   * `entityId` does not resolve on this tenant.
   */
  getIncomeStatementReport: (
    params: { entityId: Guid; basis?: "cash" | "accrual" },
    signal?: AbortSignal,
  ): Promise<IncomeStatementReport> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    if (params.basis) query.set("basis", params.basis);
    return request<IncomeStatementReport>(`/api/demo/reports/income-statement?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/income-statement/export — the same income/expense lines as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getCashFlowCsv` above gives.
   */
  getIncomeStatementCsv: async (
    params: { entityId: Guid; basis?: "cash" | "accrual" },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    if (params.basis) query.set("basis", params.basis);
    const url = `${baseUrl()}/api/demo/reports/income-statement/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/grain-inventory — ef-053-2, the CURRENT-snapshot on-hand
   * quantity per (bin, crop). D4 — no `from`/`to`: same "no window to choose" shape
   * `getLivestockInventoryReport` above takes. `entityId` is optional — same "omitted
   * resolves to the tenant's own single/oldest entity" reasoning that method's own
   * remarks give. A 404 means `entityId` (if given) does not resolve on this tenant.
   */
  getGrainInventoryReport: (
    params?: { entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<GrainInventoryReport> => {
    const query = new URLSearchParams();
    if (params?.entityId) query.set("entityId", params.entityId);
    const qs = query.toString();
    return request<GrainInventoryReport>(
      `/api/demo/reports/grain-inventory${qs ? `?${qs}` : ""}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/grain-inventory/export — ef-053-2, the snapshot as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — same `request<T>`-bypass
   * reasoning `getLivestockInventoryCsv` above gives.
   */
  getGrainInventoryCsv: async (
    params?: { entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams();
    if (params?.entityId) query.set("entityId", params.entityId);
    const qs = query.toString();
    const url = `${baseUrl()}/api/demo/reports/grain-inventory/export${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/milk-production — ef-053-3, qty/amount per (month, unit) for
   * a caller-chosen window.
   */
  getMilkProductionReport: (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<MilkProductionReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    return request<MilkProductionReport>(
      `/api/demo/reports/milk-production?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/milk-production/export — ef-053-3, the report as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getCropProductionCsv` above gives.
   */
  getMilkProductionCsv: async (
    params: { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    const url = `${baseUrl()}/api/demo/reports/milk-production/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/field-applications — ef-053-4, one row per chemical/
   * fertilizer/seed application for a caller-chosen window, optionally narrowed to one
   * field and/or one application type. `entityId` is optional — omitted, the server
   * resolves the tenant's own single/oldest entity (`FieldApplicationsReportEndpoints`'
   * own remarks), same defaulting `getProductionSalesReport` above uses. A 404 means
   * `entityId` (if given) does not resolve on this tenant.
   */
  getFieldApplicationsReport: (
    params: { from: string; to: string; entityId?: Guid; fieldId?: Guid; applicationType?: string },
    signal?: AbortSignal,
  ): Promise<FieldApplicationReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    if (params.fieldId) query.set("fieldId", params.fieldId);
    if (params.applicationType) query.set("applicationType", params.applicationType);
    return request<FieldApplicationReport>(
      `/api/demo/reports/field-applications?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/field-applications/export — ef-053-4, the log as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly, so this bypasses `request<T>`
   * (which always parses JSON), same reasoning `getProductionSalesCsv` above gives.
   */
  getFieldApplicationsCsv: async (
    params: { from: string; to: string; entityId?: Guid; fieldId?: Guid; applicationType?: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    if (params.entityId) query.set("entityId", params.entityId);
    if (params.fieldId) query.set("fieldId", params.fieldId);
    if (params.applicationType) query.set("applicationType", params.applicationType);
    const url = `${baseUrl()}/api/demo/reports/field-applications/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/partner-splits — ef-053-5, every ACTIVE partner split's
   * percentage share of ONE entity's proceeds, current snapshot. `entityId` is REQUIRED
   * (D2, same posture `getIncomeStatementReport` above takes) — unlike every
   * optional-entity sibling in this block, there is no server-side default to omit it
   * and still get an answer. A 404 means `entityId` does not resolve on this tenant.
   */
  getPartnerSplitsReport: (
    params: { entityId: Guid },
    signal?: AbortSignal,
  ): Promise<PartnerSplitReport> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    return request<PartnerSplitReport>(`/api/demo/reports/partner-splits?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/partner-splits/export — the same active-split rows as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getIncomeStatementCsv` above gives.
   */
  getPartnerSplitsCsv: async (
    params: { entityId: Guid },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    const url = `${baseUrl()}/api/demo/reports/partner-splits/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  // ---- ef-054-0a: five more report stubs, pre-registered as scaffolding (D1,
  // `ef-054-dispatch.md`) ------------------------------------------------------
  //
  // Each pair below is dead-until-wired — see this file's own ef-053-0a block above for
  // the full reasoning (same convention). Every method throws; each slice's own builder
  // REPLACES its pair with the real implementation. See `ef-054-N-build.md` for N in
  // {1, 2} and the sibling's own dispatch section for N in {3, 4, 5}.

  /**
   * GET /api/demo/reports/partner-splits-dollar — ef-054-1, each ACTIVE partner's dollar
   * share of ONE entity's net income, cash or accrual. `entityId` is REQUIRED (D2, same
   * posture `getIncomeStatementReport`/`getPartnerSplitsReport` above take) — there is no
   * server-side default to omit it and still get an answer. A 404 means `entityId` does
   * not resolve on this tenant.
   */
  getPartnerSplitsDollarReport: (
    params: { entityId: Guid; basis?: "cash" | "accrual" },
    signal?: AbortSignal,
  ): Promise<PartnerSplitDollarReport> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    if (params.basis) query.set("basis", params.basis);
    return request<PartnerSplitDollarReport>(
      `/api/demo/reports/partner-splits-dollar?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/partner-splits-dollar/export — the same per-partner dollar rows
   * as a downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getPartnerSplitsCsv` above gives.
   */
  getPartnerSplitsDollarCsv: async (
    params: { entityId: Guid; basis?: "cash" | "accrual" },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ entityId: params.entityId });
    if (params.basis) query.set("basis", params.basis);
    const url = `${baseUrl()}/api/demo/reports/partner-splits-dollar/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/gl-detail — ef-054-2, one account's own `entry_line` rows, in
   * date order, with a running balance. `accountId` is REQUIRED (D2, same posture
   * `getIncomeStatementReport` above takes) — there is no whole-farm default to fall back
   * on. Cursor-paginated, same shape `getRegister` already uses — see `GlDetailRow`'s own
   * remarks in `types.ts` for why an offset is never used here. A 404 means `accountId`
   * does not resolve on this tenant.
   */
  getGlDetailReport: (
    params: { accountId: Guid; from?: string; to?: string; limit?: number; cursor?: string },
    signal?: AbortSignal,
  ): Promise<GlDetailPage> => {
    const query = new URLSearchParams({ accountId: params.accountId });
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.limit) query.set("limit", String(params.limit));
    if (params.cursor) query.set("cursor", params.cursor);
    return request<GlDetailPage>(`/api/demo/reports/gl-detail?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/gl-detail/export — the same account's lines as a downloadable
   * file, the WHOLE window in one file (no `limit`/`cursor` — same "always the whole
   * report" posture every other report's own export route takes). RAW `text/csv`, not
   * JSON-wrapped — this route's own row-count-parity test reads the body directly, so
   * this bypasses `request<T>` (which always parses JSON), same reasoning
   * `getIncomeStatementCsv` above gives.
   */
  getGlDetailCsv: async (
    params: { accountId: Guid; from?: string; to?: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ accountId: params.accountId });
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    const url = `${baseUrl()}/api/demo/reports/gl-detail/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/trial-balance — ef-054-3, every account's actual debit/credit
   * balance as of a date, across the WHOLE tenant — no `entityId`
   * (`TrialBalanceReportEndpoints`' own remarks).
   */
  getTrialBalanceReport: (
    params: { asOf?: string },
    signal?: AbortSignal,
  ): Promise<TrialBalanceReport> => {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    return request<TrialBalanceReport>(`/api/demo/reports/trial-balance?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/trial-balance/export — ef-054-3, the worksheet as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>`
   * (which always parses JSON), same reasoning `getAgingCsv` above gives.
   */
  getTrialBalanceCsv: async (
    params: { asOf?: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams();
    if (params.asOf) query.set("asOf", params.asOf);
    const url = `${baseUrl()}/api/demo/reports/trial-balance/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/cash-receipts — ef-054-4, the cash receipts journal:
   * tenant-wide, keyset-paginated (same `cursor`/`nextCursor` shape as `getRegister`
   * above, duplicated per that file's own reasoning — the two describe unrelated reads
   * and are free to diverge).
   */
  getCashReceiptsReport: (
    params: { from: string; to: string; limit?: number; cursor?: string },
    signal?: AbortSignal,
  ): Promise<CashReceiptsPage> => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.cursor) qs.set("cursor", params.cursor);
    return request<CashReceiptsPage>(`/api/demo/reports/cash-receipts?${qs.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/cash-receipts/export — the journal as a downloadable file,
   * covering the WHOLE from/to window regardless of page size (the server walks every
   * page internally — `CashReceiptsReportRepository.GetAllForExportAsync`). RAW
   * `text/csv`, not JSON-wrapped — this route's own row-count-parity test reads the body
   * directly, so this bypasses `request<T>` (which always parses JSON), same reasoning
   * `getFieldApplicationsCsv` above gives.
   */
  getCashReceiptsCsv: async (
    params: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    const url = `${baseUrl()}/api/demo/reports/cash-receipts/export?${qs.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/payroll-tax-liability — ef-054-5, the deposit-liability summary:
   * FICA (both halves), federal and state withholding, summed per finalized, non-voided
   * pay run whose `period_end` falls in a caller-chosen window. NO `entityId` — tenant-wide,
   * same shape `getVendorPurchasesReport` above takes.
   */
  getPayrollTaxLiabilityReport: (
    params: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<PayrollTaxLiabilityReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    return request<PayrollTaxLiabilityReport>(
      `/api/demo/reports/payroll-tax-liability?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/payroll-tax-liability/export — ef-054-5, the summary as a
   * downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own row-count-parity
   * test reads the body directly — so this bypasses `request<T>` (which always parses
   * JSON), same reasoning `getVendorPurchasesCsv` above gives.
   */
  getPayrollTaxLiabilityCsv: async (
    params: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    const url = `${baseUrl()}/api/demo/reports/payroll-tax-liability/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  // ---- ef-055-0a: two more report stubs, pre-registered as scaffolding (D1,
  // `ef-055-dispatch.md`) ------------------------------------------------------
  //
  // Each pair below is dead-until-wired — see this file's own ef-054-0a block above for
  // the full reasoning (same convention). Every method throws; each slice's own builder
  // REPLACES its pair with the real implementation. See `ef-055-N-build.md` for N in
  // {1, 2}.

  /**
   * GET /api/demo/reports/reconciliation — ef-055-1, every reconciled `entry_line` on
   * one (asset) account through `asOf`, the cleared balance those lines cumulate to, and
   * the bank statement lines still open as of that same date. `accountId` is REQUIRED —
   * same posture `getGlDetailReport` takes for its own required account.
   */
  getReconciliationReport: (
    params: { accountId: Guid; asOf?: string },
    signal?: AbortSignal,
  ): Promise<ReconciliationReport> => {
    const query = new URLSearchParams({ accountId: params.accountId });
    if (params.asOf) query.set("asOf", params.asOf);
    return request<ReconciliationReport>(`/api/demo/reports/reconciliation?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/reconciliation/export — ef-055-1, the reconciled lines (only)
   * as a downloadable file. RAW `text/csv`, not JSON-wrapped — this route's own
   * row-count-parity test reads the body directly — so this bypasses `request<T>` (which
   * always parses JSON), same reasoning `getTrialBalanceCsv` above gives.
   */
  getReconciliationCsv: async (
    params: { accountId: Guid; asOf?: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ accountId: params.accountId });
    if (params.asOf) query.set("asOf", params.asOf);
    const url = `${baseUrl()}/api/demo/reports/reconciliation/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/crop-planting — ef-055-2, one row per (field, crop) planted on
   * one entity in one caller-chosen year, read directly off `field_crop`. Both `entityId`
   * and `year` are REQUIRED — unlike `getCropProductionReport` above, this route's own
   * contract keeps `entityId` required (`CropPlantingReportEndpoints`' own remarks). A 404
   * means `entityId` does not resolve on this tenant.
   */
  getCropPlantingReport: (
    params: { entityId: Guid; year: number },
    signal?: AbortSignal,
  ): Promise<CropPlantingReport> => {
    const query = new URLSearchParams({ entityId: params.entityId, year: String(params.year) });
    return request<CropPlantingReport>(`/api/demo/reports/crop-planting?${query.toString()}`, { signal });
  },

  /**
   * GET /api/demo/reports/crop-planting/export — ef-055-2, the same rows as a downloadable
   * file. RAW `text/csv`, not JSON-wrapped — this route's own row-count-parity test reads
   * the body directly — so this bypasses `request<T>` (which always parses JSON), same
   * reasoning `getCropProductionCsv` above gives.
   */
  getCropPlantingCsv: async (
    params: { entityId: Guid; year: number },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ entityId: params.entityId, year: String(params.year) });
    const url = `${baseUrl()}/api/demo/reports/crop-planting/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/reports/vendor-purchases — ef-052-3, bills' face amount, per vendor, for
   * a caller-chosen window. NO `entityId` — unlike `getProductionSalesReport`, this report
   * rolls up every bill on the tenant regardless of entity
   * (`VendorPurchasesReportRepository`'s own remarks).
   */
  getVendorPurchasesReport: (
    params: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<VendorPurchasesReport> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    return request<VendorPurchasesReport>(
      `/api/demo/reports/vendor-purchases?${query.toString()}`,
      { signal },
    );
  },

  /**
   * GET /api/demo/reports/vendor-purchases/export — ef-052-3, the summary as a downloadable
   * file. RAW `text/csv`, not JSON-wrapped like `exportLabourReport`'s own response — this
   * route's own row-count-parity test reads the body directly — so this bypasses
   * `request<T>` (which always parses JSON), same reasoning `getVendor1099Csv` above gives.
   */
  getVendorPurchasesCsv: async (
    params: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<string> => {
    const query = new URLSearchParams({ from: params.from, to: params.to });
    const url = `${baseUrl()}/api/demo/reports/vendor-purchases/export?${query.toString()}`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  /**
   * GET /api/demo/backup/export — ef-048-4, Setup's "Download backup" action: every row
   * this tenant owns, as one JSON file. Same `request<T>`-bypass reasoning as
   * `getVendor1099Csv` above — this route also needs the tenant header a plain
   * `<a href>` can't carry, and the caller builds its own Blob/anchor rather than letting
   * this helper parse the body, so the screen downloads the bytes verbatim.
   */
  getBackupExport: async (signal?: AbortSignal): Promise<string> => {
    const url = `${baseUrl()}/api/demo/backup/export`;
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { [TENANT_HEADER]: getTenant() },
      signal,
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }
    return response.text();
  },

  // ---- ef-044-4: the chart of accounts — Setup's Logins tab. Owner-only server-side
  // (403 for an employee login), same reasoning as the places/job-codes groups above —
  // an employee reaches an account only indirectly, through the resolved category on
  // their own quick-entry submissions, never through this group. ----

  /** GET /api/demo/accounts?includeArchived=true — the WHOLE chart, active and archived,
   *  in one round trip. Kept separate from `getAccounts` above (which every other
   *  account picker in the app calls with no argument and must keep reading active-only)
   *  rather than extending it, so no existing caller has to change — same shape
   *  `getPlaces`/`getJobCodes` use for their own archived disclosures, applied without
   *  touching the read call every other picker already depends on. */
  getAllAccountsIncludingArchived: (signal?: AbortSignal): Promise<Account[]> =>
    request<Account[]>("/api/demo/accounts?includeArchived=true", { signal }),

  /** POST /api/demo/accounts. A 409 means an account with that number already exists —
   *  compared exactly, and against archived accounts too (AccountEndpoints.cs). */
  createAccount: (body: CreateAccountRequest, signal?: AbortSignal): Promise<AccountWriteResult> =>
    request<AccountWriteResult>("/api/demo/accounts", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/accounts/{id} — rename only; see `UpdateAccountRequest`'s own
   *  remarks for why number and kind are not offered here. */
  renameAccount: (
    id: Guid,
    body: UpdateAccountRequest,
    signal?: AbortSignal,
  ): Promise<AccountWriteResult> =>
    request<AccountWriteResult>(`/api/demo/accounts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/accounts/{id}/archive — ef-044-4. Reversible (`restoreAccount`
   *  below), unlike `archivePlace`/`archiveJobCode`'s idempotent DELETE shape: a second
   *  call against an already-archived account is refused with 404, same posture as
   *  `archiveHerd`. */
  archiveAccount: (id: Guid, signal?: AbortSignal): Promise<AccountWriteResult> =>
    request<AccountWriteResult>(`/api/demo/accounts/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/accounts/{id}/restore — the other direction. Idempotent: a second
   *  call, or one against an already-active account, still succeeds. */
  restoreAccount: (id: Guid, signal?: AbortSignal): Promise<AccountWriteResult> =>
    request<AccountWriteResult>(`/api/demo/accounts/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      signal,
    }),

  /**
   * POST /api/demo/setup/accounts/import — ef-049-1, a raw CSV chart-of-accounts import
   * (header `number,name,kind`). The body is sent as `text/csv`, not JSON — same
   * `Content-Type` override `importStatement` above uses, for the same reason. ALL OR
   * NOTHING: a 409 means a duplicate `number` (within the file or against an existing
   * account) and nothing was written; a 400 can mean either an unrecognized header or a
   * formula-leading cell, both whole-file refusals (`AccountImportEndpoints.cs`).
   */
  importAccounts: (csv: string, signal?: AbortSignal): Promise<AccountImportResult> =>
    request<AccountImportResult>("/api/demo/setup/accounts/import", {
      method: "POST",
      body: csv,
      headers: { "Content-Type": "text/csv" },
      signal,
    }),

  /**
   * GET /api/demo/reports/payroll-register — ef-044-3, the one new read route this slice
   * adds. Two mutually exclusive modes: `{ payRunId }` answers for exactly one finalized
   * run; `{ from, to, entityId }` sums every finalized, non-voided run on that entity whose
   * period overlaps the window. Owner-only, same session-authenticated discipline as
   * `getReconcileSummary` above.
   *
   * A 400 naming `payRunId` means that run is still a draft, or was voided — the field
   * name lets a caller show that sentence beside the picker that chose it, same
   * `problemMessage` discipline every other validation failure in this client already
   * follows. A 404 means the run — or, in range mode, the entity — does not resolve on
   * this tenant.
   */
  getPayrollRegister: (
    query: { payRunId: Guid } | { from: string; to: string; entityId?: Guid },
    signal?: AbortSignal,
  ): Promise<PayrollRegisterReport> => {
    const qs = new URLSearchParams();
    if ("payRunId" in query) {
      qs.set("payRunId", query.payRunId);
    } else {
      qs.set("from", query.from);
      qs.set("to", query.to);
      // Omit to ask the server for ITS OWN default entity — same "omit for the server's
      // own default" discipline as getLabourReport's window, for a tenant with exactly
      // one entity, which is every farm on the booth tenant today.
      if (query.entityId) qs.set("entityId", query.entityId);
    }
    return request<PayrollRegisterReport>(
      `/api/demo/reports/payroll-register?${qs.toString()}`,
      { signal },
    );
  },

  /**
   * POST /api/demo/books/documents (ef-043-4). A 400 naming `accountId` means `kind`
   * doesn't match that account's own kind (invoice/income, bill/expense); a 400 naming
   * `counterpartyId` means it does not resolve on this tenant.
   */
  createDocument: (body: CreateDocumentRequest, signal?: AbortSignal): Promise<Document> =>
    request<Document>("/api/demo/books/documents", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * DELETE /api/demo/books/documents/{id} (ef-043-4) — a real delete, same reasoning as
   * `deleteVendor`: `document` has no void/status column to soft-delete into. Cascades to
   * any settlement (none exist yet — that is ef-043-5).
   *
   * `idempotencyKey` (ef-048-2b) rides as the `Idempotency-Key` header a double-tapped
   * void button would otherwise repeat — see `IdempotencyGuard`'s own remarks for the
   * server-side replay contract this opts a caller into.
   */
  voidDocument: (id: Guid, idempotencyKey?: string, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/books/documents/${encodeURIComponent(id)}`, {
      method: "DELETE",
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /**
   * POST /api/demo/books/documents/{id}/settle (ef-043-5) — partial settlement is
   * allowed; a 409 means the offered amount exceeds the document's own outstanding
   * balance, or the document has been voided.
   *
   * `idempotencyKey` (ef-048-2b) — see `voidDocument`'s own remarks.
   */
  settleDocument: (
    id: Guid,
    body: SettleDocumentRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<SettleDocumentResponse> =>
    request<SettleDocumentResponse>(`/api/demo/books/documents/${encodeURIComponent(id)}/settle`, {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /**
   * POST /api/demo/books/reconcile/{accountId}/import — ef-043-1, a raw bank-statement
   * CSV import into `bank_statement_line`. The body is sent as `text/csv`, not JSON —
   * the `Content-Type` override below is deliberate, same reasoning `request`'s own
   * FormData branch gives for why a non-default body needs its header stated explicitly.
   * Owner-only, same session-authenticated discipline as `getBooksActivity` above.
   */
  importStatement: (accountId: Guid, csv: string, signal?: AbortSignal): Promise<ImportStatementResult> =>
    request<ImportStatementResult>(`/api/demo/books/reconcile/${accountId}/import`, {
      method: "POST",
      body: csv,
      headers: { "Content-Type": "text/csv" },
      signal,
    }),

  // ---- ef-058 anchors: employee create/edit/rate history + bank feed ---------------------
  //
  // Thin wrappers over frozen routes (no logic to build, so they are real, not stubs). The
  // routes behind them answer 501 until their slice lands — see `ef-058-dispatch.md`.

  /** POST /api/demo/employees — ef-058-2. OWNER only; 201 with the new roster row.
   *  `idempotencyKey` (ef-048-2b) rides as the `Idempotency-Key` header a double-clicked Save
   *  would otherwise repeat — see `voidDocument`'s own remarks. */
  createEmployee: (
    body: CreateEmployeeRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<PayrollEmployee> =>
    request<PayrollEmployee>("/api/demo/employees", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /** PATCH /api/demo/employees/{id} — ef-058-2. A true partial update; `active:false`
   *  deactivates (there is no DELETE). */
  updateEmployee: (id: Guid, body: UpdateEmployeeRequest, signal?: AbortSignal): Promise<PayrollEmployee> =>
    request<PayrollEmployee>(`/api/demo/employees/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/employees/{id}/rates — ef-058-2, newest effective date first. */
  getEmployeeRates: (id: Guid, signal?: AbortSignal): Promise<EmployeeRateRow[]> =>
    request<EmployeeRateRow[]>(`/api/demo/employees/${encodeURIComponent(id)}/rates`, { signal }),

  /** POST /api/demo/employees/{id}/rates — ef-058-2, a dated rate change (201). A back-dated
   *  change under a finalized run succeeds with a warning and never rewrites the run. */
  changeEmployeeRate: (
    id: Guid,
    body: ChangeEmployeeRateRequest,
    signal?: AbortSignal,
  ): Promise<ChangeEmployeeRateResponse> =>
    request<ChangeEmployeeRateResponse>(`/api/demo/employees/${encodeURIComponent(id)}/rates`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/books/reconcile/{accountId}/mapping — ef-058-6. Resolves `null` when no
   *  mapping is saved (the route answers 204). */
  getBankImportMapping: (accountId: Guid, signal?: AbortSignal): Promise<BankImportMapping | null> =>
    request<BankImportMapping | undefined>(`/api/demo/books/reconcile/${accountId}/mapping`, { signal }).then(
      (mapping) => mapping ?? null,
    ),

  /** PUT /api/demo/books/reconcile/{accountId}/mapping — ef-058-6. */
  saveBankImportMapping: (
    accountId: Guid,
    body: BankImportMappingRequest,
    signal?: AbortSignal,
  ): Promise<BankImportMapping> =>
    request<BankImportMapping>(`/api/demo/books/reconcile/${accountId}/mapping`, {
      method: "PUT",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/books/reconcile/{accountId}/auto-match — ef-058-7. */
  autoMatchStatement: (accountId: Guid, signal?: AbortSignal): Promise<AutoMatchResult> =>
    request<AutoMatchResult>(`/api/demo/books/reconcile/${accountId}/auto-match`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/books/reconcile/{accountId}/add-entry — ef-058-8 (201). `idempotencyKey`
   *  — see `createEmployee`. */
  addStatementEntry: (
    accountId: Guid,
    body: AddStatementEntryRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<AddStatementEntryResult> =>
    request<AddStatementEntryResult>(`/api/demo/books/reconcile/${accountId}/add-entry`, {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /**
   * GET /api/demo/books/reconcile/lines — ef-043-1, one account's imported statement
   * lines, most recent first. Owner-only, same session-authenticated discipline as
   * `getBooksActivity` above.
   */
  getStatementLines: (
    params: { accountId: Guid; from?: string; to?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<BankStatementLine[]> => {
    const qs = new URLSearchParams();
    qs.set("accountId", params.accountId);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    return request<BankStatementLine[]>(`/api/demo/books/reconcile/lines?${qs.toString()}`, { signal });
  },

  /**
   * POST /api/demo/books/close-year — ef-043-8, closes a fiscal year: zeroes every
   * income/expense account's balance for the entity+year into 3900 Retained Earnings and
   * locks the entity's period through December 31 of that year. Owner-only, same
   * session-authenticated discipline as `getBooksActivity` above.
   */
  closeYear: (entityId: Guid, fiscalYear: number, signal?: AbortSignal): Promise<CloseYearResult> =>
    request<CloseYearResult>("/api/demo/books/close-year", {
      method: "POST",
      body: JSON.stringify({ entityId, fiscalYear }),
      signal,
    }),

  /**
   * POST /api/demo/books/close-year/unlock — ef-043-8, lifts a fiscal-year lock. Never
   * reverses the close-year entry itself — see BooksCloseYearRepository's own remarks.
   * Owner-only, same session-authenticated discipline as `getBooksActivity` above.
   */
  unlockYear: (entityId: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>("/api/demo/books/close-year/unlock", {
      method: "POST",
      body: JSON.stringify({ entityId }),
      signal,
    }),

  /**
   * GET /api/demo/books/close-year/status — ef-043-8, this entity's current lock date, or
   * null if unlocked. Owner-only, same session-authenticated discipline as
   * `getBooksActivity` above.
   */
  getYearLock: (entityId: Guid, signal?: AbortSignal): Promise<YearLockStatus> =>
    request<YearLockStatus>(`/api/demo/books/close-year/status?entityId=${encodeURIComponent(entityId)}`, { signal }),

  /**
   * GET /api/demo/usage-counts — the aggregate-only, PUBLIC counterpart to
   * `getEntries` (feature/auth). Returns how many entries exist per vendor/account
   * and nothing else; see useUsageCounts.ts, which is the one caller (the anonymous
   * `/entry` visitor picker never reads the full entry list any more).
   */
  /**
   * GET /api/demo/filing-status — which quarters are filed, when each closes and when
   * its return is due. Session-authenticated: a filing history is financial detail.
   */
  getFilingStatus: (entityId: string, year: number, signal?: AbortSignal): Promise<FilingCalendar> =>
    request<FilingCalendar>(`/api/demo/filing-status?entityId=${encodeURIComponent(entityId)}&year=${year}`, { signal }),

  getUsageCounts: (signal?: AbortSignal): Promise<UsageCounts> =>
    request<UsageCounts>("/api/demo/usage-counts", { signal }),

  /**
   * GET /api/demo/reports/profit-loss — the Reports screen's P&L card, replacing
   * reports-fixtures.ts. Cash basis only; see ProfitAndLossReport and Reports.tsx for
   * why there is no accrual figure to ask this for.
   */
  getProfitAndLoss: (basis: "cash" | "accrual" = "cash", signal?: AbortSignal): Promise<ProfitAndLossReport> =>
    request<ProfitAndLossReport>(`/api/demo/reports/profit-loss?basis=${basis}`, { signal }),

  /**
   * GET /api/demo/reports/balance-sheet — the Reports screen's Net worth card, and the
   * Dashboard's Cash on hand. ef-002 / ADR 0005: before it, neither number could be read
   * from the ledger at all.
   */
  getBalanceSheet: (signal?: AbortSignal): Promise<BalanceSheetReport> =>
    request<BalanceSheetReport>("/api/demo/reports/balance-sheet", { signal }),

  /**
   * POST /api/demo/entries — the visitor's entry.
   *
   * TIMED OUT, unlike every other call here, because this is the one a stranger is standing
   * over. With the api container PAUSED — a hung back end rather than a dead one, which is
   * what a degraded link looks like — the submit button read "Submitting…" and stayed
   * disabled at 2s, 9s and 22s, with no error and no outbox. Measured 2026-09-02. A visitor
   * hands the phone back and walks away; that is worse than an error, and it is the same
   * "permanent spinner is indistinguishable from slow" shape already fixed on /year-end.
   *
   * 15s is deliberately generous — the spec budgets ~10s for the WHOLE entry, so anything
   * near that is already a bad venue link — while still ending well inside a booth
   * conversation.
   *
   * The timeout deliberately does NOT queue the entry. A timeout is genuinely ambiguous:
   * the server may have recorded it. `clientId` is a LOCAL queue key and is never sent, so
   * the API has no idempotency to dedupe a replay, and auto-retrying would put a second row
   * on the wall. QuickEntry turns this into a plain-language message and lets the visitor
   * decide — the same reasoning as the 500 branch. See ApiError.neverReachedTheServer for
   * the statuses that ARE safe to hold.
   */
  createEntry: (body: CreateEntryRequest, signal?: AbortSignal): Promise<CreateEntryResponse> =>
    request<CreateEntryResponse>("/api/demo/entries", {
      method: "POST",
      body: JSON.stringify(body),
      signal: signal ?? AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
    }),

  /**
   * GET /api/demo/books/register — ef-042-1, OWNER only. A check-register view of one
   * instrument (asset-kind) account: its lines in date order, a running balance, and a
   * `red` flag for any row this view could not classify honestly (see `RegisterEntry`).
   *
   * Cursor pagination, not offset — pass back the previous page's `nextCursor` verbatim,
   * opaque, never parsed or constructed client-side (`BooksRegisterRepository`'s own
   * remarks: an offset page cannot keep a running balance stable against concurrent
   * writes, a keyset cursor can).
   *
   * `order` (ef-048-6): `"asc"` (oldest first, the API's own default when omitted) or
   * `"desc"` (newest first). A cursor minted under one direction is meaningless fed back
   * under the other — `useRegister.ts` always sends the SAME `order` on every fetch for a
   * given account, first page and `loadMore` alike.
   */
  getRegister: (
    params: { accountId: Guid; from?: string; to?: string; limit?: number; cursor?: string; order?: "asc" | "desc" },
    signal?: AbortSignal,
  ): Promise<{ entries: RegisterEntry[]; nextCursor: string | null }> => {
    const qs = new URLSearchParams({ accountId: params.accountId });
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.cursor) qs.set("cursor", params.cursor);
    if (params.order) qs.set("order", params.order);
    return request<{ entries: RegisterEntry[]; nextCursor: string | null }>(
      `/api/demo/books/register?${qs.toString()}`,
      { signal },
    );
  },

  /**
   * POST /api/demo/receipts/analyze — the first beat of the receipt demo.
   *
   * Sent as multipart FormData, NOT JSON: the request helper deliberately omits
   * Content-Type for FormData so the browser supplies the multipart boundary.
   *
   * The server reads the image, analyzes it, and DISCARDS it — nothing is stored.
   * Worth knowing when a stranger at a booth hands over their own receipt.
   */
  analyzeReceipt: (
    file: Blob,
    signal?: AbortSignal,
    /**
     * Overrides the filename sent with the upload. `File` already carries a
     * name; a bare `Blob` does not — and the offline scan queue
     * (`offline/scanQueue.ts`) stores its held photos typed as `Blob` rather
     * than `File`, to keep the queue's record shape independent of whichever
     * DOM API produced the image, so its drain path supplies the original
     * filename explicitly instead of this silently falling back to
     * "receipt.jpg".
     */
    filename?: string,
  ): Promise<ReceiptAnalysis> => {
    const form = new FormData();
    const name = filename || (file instanceof File ? file.name : "") || "receipt.jpg";
    form.append("image", file, name);
    return request<ReceiptAnalysis>("/api/demo/receipts/analyze", {
      method: "POST",
      body: form,
      // Bounded, like createEntry. A caller-supplied signal wins: the offline queue's
      // drain passes its own, and it should not be cut short by this default.
      signal: signal ?? AbortSignal.timeout(ANALYZE_TIMEOUT_MS),
    });
  },

  /**
   * POST /api/demo/mappings/confirm — records that a human accepted or corrected
   * a vendor to account mapping. This is the write that turns the next scan of the
   * same vendor into a deterministic lookup rather than another model call.
   */
  confirmMapping: (
    body: { vendorId: Guid; accountId: Guid },
    signal?: AbortSignal,
  ): Promise<void> =>
    request<void>("/api/demo/mappings/confirm", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/reset — clears entries + learned mappings for the
   *  calling request's tenant only (RLS-scoped; see BoothReset.tsx). */
  resetDemo: (signal?: AbortSignal): Promise<void> =>
    request<void>("/api/demo/reset", { method: "POST", signal }),

  // ---- Employee time entry -------------------------------------------------
  //
  // NONE of these takes an employee id, and none ever should. The server resolves the
  // subject from the session cookie; a parameter here that named an employee would let
  // one worker file — or read — a colleague's time. See api/types.ts's note above the
  // time types, and TimeEndpoints' class remarks.
  //
  // The first four are what an EMPLOYEE login may call; the last three are the owner's
  // review side. They sit on one object because they are one route group, not because
  // one caller uses them all — the API refuses the wrong role either way (403), which is
  // the control. Splitting them into two client objects would only look like security.

  /** GET /api/demo/time/mine — the calling employee's own recent days, their running
   *  shift if any, and this business's recording mode. Their whole screen in one call. */
  getMyTime: (signal?: AbortSignal): Promise<MyTime> =>
    request<MyTime>("/api/demo/time/mine", { signal }),

  /**
   * GET /api/demo/time/job-codes — ef-024: the farm's own ACTIVE job codes, for the
   * worker's own allocation panel. Employee OR owner (`TimeEndpoints.cs`'s own remark on
   * why this admits either role) — unlike `getJobCodes` below, which is owner-only.
   * Never `includeArchived`: an archived code is retired vocabulary, not something either
   * role should be offered again.
   */
  getWorkerJobCodes: (signal?: AbortSignal): Promise<JobCode[]> =>
    request<JobCode[]>("/api/demo/time/job-codes", { signal }),

  /**
   * GET /api/demo/schedule/mine — ef-040-4: the calling employee's own upcoming shifts,
   * in the fixed 30-day window the ROUTE decides (`ScheduleEndpoints.MyScheduleWindowDays`)
   * — this screen never asks for a wider one, and never sends `from`/`to`. Read-only; shift
   * CRUD is the owner's board (ef-040-3, a different screen).
   */
  getMySchedule: (signal?: AbortSignal): Promise<Shift[]> =>
    request<Shift[]>("/api/demo/schedule/mine", { signal }),

  /**
   * GET /api/demo/time-off/mine — ef-040-4: the calling employee's own time-off requests,
   * newest first, in the route's own fixed 90-day window
   * (`ScheduleEndpoints.TimeOffHistoryDefaultWindowDays`), carrying the owner's decision
   * (`reviewedAt`/`reviewNote`) once made. A real server read on every call — see
   * worker-schedule.spec.ts's own header on why this list must survive a reload rather
   * than living only in this screen's state.
   */
  getMyTimeOff: (signal?: AbortSignal): Promise<TimeOffRequest[]> =>
    request<TimeOffRequest[]>("/api/demo/time-off/mine", { signal }),

  /**
   * POST /api/demo/time-off — ef-040-4: submit a time-off request. Never carries an
   * `employeeId` — the session names the requester (`SubmitTimeOffRequest`'s own remark),
   * same discipline as `punchIn`/`punchOut`/`recordManualTime` above.
   */
  submitTimeOff: (body: SubmitTimeOffRequest, signal?: AbortSignal): Promise<TimeOffRequest> =>
    request<TimeOffRequest>("/api/demo/time-off", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * POST /api/demo/time/punch-in — start a shift.
   *
   * `where` is optional and stays optional all the way down: the body is omitted
   * entirely when the browser gave nothing, rather than sent with nulls. A 409 means a
   * shift is already running (the partial unique index makes that true under a race, not
   * merely usually).
   */
  punchIn: (where?: PunchRequest, signal?: AbortSignal): Promise<PunchInResult> =>
    request<PunchInResult>("/api/demo/time/punch-in", {
      method: "POST",
      body: JSON.stringify(where ?? {}),
      signal: signal ?? AbortSignal.timeout(PUNCH_TIMEOUT_MS),
    }),

  /** POST /api/demo/time/punch-out — close the running shift. Hours come back DERIVED
   *  from the server's two timestamps; the phone never computes or sends them. */
  punchOut: (where?: PunchRequest, signal?: AbortSignal): Promise<PunchOutResult> =>
    request<PunchOutResult>("/api/demo/time/punch-out", {
      method: "POST",
      body: JSON.stringify(where ?? {}),
      signal: signal ?? AbortSignal.timeout(PUNCH_TIMEOUT_MS),
    }),

  /** POST /api/demo/time/manual — type hours for a day. Never carries a location: a
   *  person entering Tuesday's hours on Wednesday has no meaningful coordinate to give. */
  recordManualTime: (body: ManualTimeRequest, signal?: AbortSignal): Promise<{ id: Guid }> =>
    request<{ id: Guid }>("/api/demo/time/manual", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * POST /api/demo/time/sign — the worker confirms this period's hours are right.
   *
   * Sends only HOW they signed. The period and the totals are the server's, recomputed
   * inside the write: a request that carried the figures would let a worker sign a total
   * that had already moved on, which is the one thing the signature exists to rule out.
   *
   * Returns the period fresh, so the screen renders what was STORED rather than what it
   * had on the page a moment ago.
   */
  signTimesheet: (body: SignTimesheetRequest, signal?: AbortSignal): Promise<PayPeriod> =>
    request<PayPeriod>("/api/demo/time/sign", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/time/pending — OWNER only. Everything awaiting review, oldest first,
   *  so the queue drains rather than accumulating at the bottom. */
  getPendingTime: (signal?: AbortSignal): Promise<TimeEntry[]> =>
    request<TimeEntry[]>("/api/demo/time/pending", { signal }),

  /** POST /api/demo/time/{id}/review — OWNER only. A 409 means somebody already decided
   *  this row; it is not retryable, and the queue should be reloaded rather than retried. */
  reviewTime: (id: Guid, body: ReviewTimeRequest, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/time/${encodeURIComponent(id)}/review`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/time/apply — OWNER only. Rolls APPROVED hours for a period onto
   *  employee.hours_this_period. Submitted time is a claim, not a fact, and does not
   *  count. */
  applyApprovedHours: (
    body: ApplyHoursRequest,
    signal?: AbortSignal,
  ): Promise<ApplyHoursResult> =>
    request<ApplyHoursResult>("/api/demo/time/apply", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * GET /api/demo/time/report — OWNER only. Approved hours per person across a window.
   *
   * Omit `from`/`to` and the server derives the current pay period and says which one it
   * used, on the response. The dates are NOT defaulted here: a client that guessed the
   * fortnight would eventually guess a different one than the server totalled, and the
   * screen would print correct figures under wrong dates.
   */
  /**
   * GET /api/demo/time/export — OWNER only. The bookkeeper's CSV for a window.
   *
   * Returns the file as a STRING the caller turns into a Blob, mirroring the wage detail
   * rather than serving text/csv directly. The reason is the same one PaidLeave gives: the
   * bytes are the server writer's verbatim, and a client that rebuilt them from rows would
   * be a second implementation of the file format — free to disagree with the first about a
   * comma in somebody's name.
   *
   * Unlike the hours report beside it, this carries EVERY status.
   */
  exportTimesheets: (
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<TimesheetExport> =>
    request<TimesheetExport>(
      range
        ? `/api/demo/time/export?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "/api/demo/time/export",
      { signal },
    ),

  /**
   * GET /api/payroll/paid-leave/programs — which state paid-leave returns exist.
   *
   * The year is always sent. Letting the server pick means a screen can print a return name
   * beside a year it did not choose, and the response echoes whichever year answered so the
   * two can never silently differ.
   */
  listPaidLeavePrograms: (year: number, signal?: AbortSignal): Promise<StatePaidLeavePrograms> =>
    request<StatePaidLeavePrograms>(
      `/api/payroll/paid-leave/programs?year=${encodeURIComponent(String(year))}`,
      { signal },
    ),

  getApprovedHoursReport: (
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<HoursReport> =>
    request<HoursReport>(
      range
        ? `/api/demo/time/report?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "/api/demo/time/report",
      { signal },
    ),

  /**
   * GET /api/demo/reports/labour — OWNER only. ef-038-1: hours (and an hourly-only
   * estimated cost) per job code, over a window the farmer chooses. Same "omit for the
   * server's own default window" discipline as `getApprovedHoursReport` above.
   */
  getLabourReport: (
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<LabourReport> =>
    request<LabourReport>(
      range
        ? `/api/demo/reports/labour?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "/api/demo/reports/labour",
      { signal },
    ),

  /**
   * GET /api/demo/reports/labour/export — OWNER only. ef-044-5: the labour-by-job-code
   * report, as a CSV. Returns the file as a STRING the caller turns into a Blob, same
   * "the bytes are the server writer's verbatim" discipline as `exportTimesheets` above —
   * a client that rebuilt it from rows would be a second implementation of the file
   * format, free to disagree with the first about a comma in somebody's name.
   */
  exportLabourReport: (
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<LabourReportExport> =>
    request<LabourReportExport>(
      range
        ? `/api/demo/reports/labour/export?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "/api/demo/reports/labour/export",
      { signal },
    ),

  /**
   * GET /api/demo/time/runs — OWNER only. ef-034: the payroll run history.
   *
   * Omit `range` and the server answers with its OWN rolling window (today back
   * `MaxReportDays`), never a window this client invented — same discipline as
   * `getApprovedHoursReport` above. Passing an EXACT period's `{from, to}` is how
   * PayRuns.tsx finds whether a run already exists for the period on screen: the server
   * matches on `period_start BETWEEN from AND to`, so a period whose start equals `from`
   * is always included.
   */
  getPayRuns: (
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<GetPayRunsResponse> =>
    request<GetPayRunsResponse>(
      range
        ? `/api/demo/time/runs?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
        : "/api/demo/time/runs",
      { signal },
    ),

  /**
   * POST /api/demo/payroll/runs/{id}/finalize — OWNER only. ef-027/ef-034: the one write
   * that turns an applied run's hours into withholding and a net check.
   *
   * A ONE-WAY DOOR — there is no un-finalize route anywhere in this API. A 400 means at
   * least one hourly employee on this run has no `hourlyRate` set (the message names every
   * one of them); a 409 means somebody already finalized this exact run — its own
   * `finalizedAt` is the body. Neither is retried here; the caller decides what to do next.
   */
  finalizePayRun: (id: Guid, signal?: AbortSignal): Promise<PayRunFinalizeResult> =>
    request<PayRunFinalizeResult>(`/api/demo/payroll/runs/${encodeURIComponent(id)}/finalize`, {
      method: "POST",
      signal,
    }),

  /**
   * POST /api/demo/payroll/runs/{id}/void — OWNER only. ef-030: undoes a mistaken
   * finalize WITHOUT touching a single dollar it already computed — `finalizedAt` and
   * every line's own money stay on file, kept as evidence. What changes is that the
   * period opens back up: `apply` on the identical window no longer throws.
   *
   * A 400 means the reason was empty (or over 200 characters); a 409 means the run was
   * never finalized, or was already voided elsewhere — its own `voidedAt` is the body.
   * Neither is retried here; the caller decides what to do next.
   */
  voidPayRun: (id: Guid, body: VoidPayRunRequest, signal?: AbortSignal): Promise<VoidPayRunResult> =>
    request<VoidPayRunResult>(`/api/demo/payroll/runs/${encodeURIComponent(id)}/void`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * POST /api/demo/payroll/runs/{id}/post — OWNER only. ef-033: turns a FINALIZED run's
   * already-fixed totals into ONE balanced ledger entry — no new money math happens here,
   * see `PayRunPostEndpoints.cs`'s own remarks; this only resolves each line's account by
   * `account.payroll_role` and writes it.
   *
   * A 400 (ValidationProblem) names the payroll-role account(s) missing from this tenant's
   * chart of accounts — nothing was posted; a 409 means the run was not finalized, was
   * voided, or was already posted — its own `postedEntryId` is the body. Neither is
   * retried here; the caller decides what to do next, same discipline as
   * `finalizePayRun`/`voidPayRun` above.
   *
   * `idempotencyKey` (ef-048-2b) — see `voidDocument`'s own remarks.
   */
  postPayRun: (id: Guid, idempotencyKey?: string, signal?: AbortSignal): Promise<PayRunPostResult> =>
    request<PayRunPostResult>(`/api/demo/payroll/runs/${encodeURIComponent(id)}/post`, {
      method: "POST",
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /**
   * POST /api/demo/payroll/runs/{runId}/lines/{lineId}/print — OWNER only. ef-047-4:
   * allocates the next check number for this line's own entity (locked, so two
   * concurrent prints against the same entity can never collide) and prints it.
   *
   * A 409 means the run is not finalized yet, or this line is already printed and not
   * voided — its own `checkNumber`/`printedAt` is the body. A voided line's reprint
   * succeeds and clears its void state; the caller decides what to do next.
   */
  printPayCheck: (runId: Guid, lineId: Guid, signal?: AbortSignal): Promise<PayRunPrintResult> =>
    request<PayRunPrintResult>(
      `/api/demo/payroll/runs/${encodeURIComponent(runId)}/lines/${encodeURIComponent(lineId)}/print`,
      { method: "POST", signal },
    ),

  /**
   * GET /api/demo/payroll/runs/{runId}/lines/{lineId}/print — OWNER only. ef-047-4: the
   * render payload for a line already printed. A 409 means this line has never been
   * printed — call `printPayCheck` first.
   */
  getPayCheckPrint: (runId: Guid, lineId: Guid, signal?: AbortSignal): Promise<PayRunPrintResult> =>
    request<PayRunPrintResult>(
      `/api/demo/payroll/runs/${encodeURIComponent(runId)}/lines/${encodeURIComponent(lineId)}/print`,
      { signal },
    ),

  /**
   * POST /api/demo/payroll/runs/{runId}/lines/{lineId}/void — OWNER only. ef-047-4:
   * voids this line's CURRENT print. The retired check number is never reissued — a
   * later reprint (`printPayCheck`) always gets a fresh one.
   *
   * A 400 means the reason was empty (or over 200 characters); a 409 means this line has
   * never been printed, or was already voided elsewhere — its own `voidedAt` is the body.
   * Neither is retried here; the caller decides what to do next.
   */
  voidPayCheck: (
    runId: Guid,
    lineId: Guid,
    body: VoidCheckRequest,
    signal?: AbortSignal,
  ): Promise<VoidCheckResult> =>
    request<VoidCheckResult>(
      `/api/demo/payroll/runs/${encodeURIComponent(runId)}/lines/${encodeURIComponent(lineId)}/void`,
      { method: "POST", body: JSON.stringify(body), signal },
    ),

  /**
   * PATCH /api/demo/employees/{id}/pay — OWNER only. ef-027/ef-034: sets the rate a
   * Finalize run derives an hourly employee's gross from. A partial update in spirit only
   * — both fields are required on the wire because the server validates them together
   * (`payBasis` must be "hourly"/"salary"; `hourlyRate` may be null to clear it, but must
   * not be negative).
   */
  updateEmployeePay: (
    id: Guid,
    body: UpdateEmployeePayRequest,
    signal?: AbortSignal,
  ): Promise<PayrollEmployee> =>
    request<PayrollEmployee>(`/api/demo/employees/${encodeURIComponent(id)}/pay`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /**
   * PATCH /api/demo/employees/{id}/w4 — OWNER only. ef-038-4: sets an employee's Form W-4
   * election. A TRUE partial update — unlike `updateEmployeePay` above, any field left off
   * `body` keeps its current server-side value rather than being required on every call.
   */
  updateEmployeeW4: (
    id: Guid,
    body: UpdateEmployeeW4Request,
    signal?: AbortSignal,
  ): Promise<PayrollEmployee> =>
    request<PayrollEmployee>(`/api/demo/employees/${encodeURIComponent(id)}/w4`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- Saved places (ef-021) ------------------------------------------------
  //
  // Owner-only server-side (403 for an employee login), same reasoning as the time-review
  // routes above — an employee reads a place only indirectly, through the resolved
  // `startPlace`/`endPlace` on their own punches, never through this group.

  /** GET /api/demo/places — the farm's own saved locations. `includeArchived` defaults to
   *  false server-side; this client always asks for both and lets `usePlaces` split them,
   *  so the archived disclosure never needs a second round trip to open. */
  getPlaces: (includeArchived = false, signal?: AbortSignal): Promise<Place[]> =>
    request<Place[]>(
      `/api/demo/places${includeArchived ? "?includeArchived=true" : ""}`,
      { signal },
    ),

  /**
   * POST /api/demo/books/register — ef-042-2, OWNER only. Two mutually exclusive shapes
   * on one body (`PostRegisterEntryRequest`): Simple (`categoryAccountId` + `amount`,
   * only when `registerAccountId` is the tenant's own default cash account) or Explicit
   * (`lines`, sent verbatim — a transfer or a split; the register account's own leg is
   * one of `lines`, like every other leg). Same submit-timeout convention as
   * `createEntry`: a financial write should never hang the compose flow indefinitely.
   */
  postRegisterEntry: (
    body: PostRegisterEntryRequest,
    signal?: AbortSignal,
  ): Promise<{ id: Guid }> =>
    request<{ id: Guid }>("/api/demo/books/register", {
      method: "POST",
      body: JSON.stringify(body),
      signal: signal ?? AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
    }),

  /**
   * POST /api/demo/books/register/{entryLineId}/cleared — ADR 0010 §2, ef-056-3.
   * ef-056-0a anchor (Spec Architect, frozen contract): the path and body shape below do
   * not change. 400 when the row is already tied to an imported-statement match — the
   * caller (RegisterEntryDialog/the register row) shows that `detail` text, it does not
   * retry or silently swallow it.
   */
  setEntryLineCleared: (
    entryLineId: Guid,
    cleared: boolean,
    signal?: AbortSignal,
  ): Promise<{ entryLineId: Guid; cleared: boolean }> =>
    request<{ entryLineId: Guid; cleared: boolean }>(
      `/api/demo/books/register/${encodeURIComponent(entryLineId)}/cleared`,
      {
        method: "POST",
        body: JSON.stringify({ cleared }),
        signal,
      },
    ),

  // ---- ef-064-7 anchor: register quantity -----------------------------------------------------

  /** PUT /api/demo/books/register/{entryLineId}/quantity - set, replace or CLEAR (both null) the quantity + unit of a register
   *  entry; it lands on the entry's single other leg. 400 naming `quantity` / `unit` (bad value, one without the other, or a
   *  split entry); 404 when the line does not resolve. Allowed inside a closed period. */
  setRegisterQuantity: (
    entryLineId: Guid,
    body: SetRegisterQuantityRequest,
    signal?: AbortSignal,
  ): Promise<SetRegisterQuantityResult> =>
    request<SetRegisterQuantityResult>(`/api/demo/books/register/${encodeURIComponent(entryLineId)}/quantity`, {
      method: "PUT",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-062 anchor: register check printing -----------------------------------------------

  /** PUT /api/demo/books/register/{entryLineId}/print-to-check - the per-payment "print this check" flag.
   *  400: not a payment from an asset account / no payee; 409: clearing it while a check stands. */
  setPrintToCheck: (entryLineId: Guid, printToCheck: boolean, signal?: AbortSignal): Promise<SetPrintToCheckResult> =>
    request<SetPrintToCheckResult>(`/api/demo/books/register/${encodeURIComponent(entryLineId)}/print-to-check`, {
      method: "PUT",
      body: JSON.stringify({ printToCheck }),
      signal,
    }),

  /** POST /api/demo/books/register/{entryLineId}/print-check - numbers and prints ONE check (also the reprint
   *  after a void). 400: not flagged / no payee; 409: a check already stands. */
  printRegisterCheck: (entryLineId: Guid, signal?: AbortSignal): Promise<RegisterCheck> =>
    request<RegisterCheck>(`/api/demo/books/register/${encodeURIComponent(entryLineId)}/print-check`, {
      method: "POST",
      signal,
    }),

  /** GET /api/demo/books/register/{entryLineId}/check - the line's latest check (409 when never printed). A
   *  read: it never allocates a number. */
  getRegisterCheck: (entryLineId: Guid, signal?: AbortSignal): Promise<RegisterCheck> =>
    request<RegisterCheck>(`/api/demo/books/register/${encodeURIComponent(entryLineId)}/check`, { signal }),

  /** POST /api/demo/books/register/{entryLineId}/void-check - the number stays on record, never reissued. */
  voidRegisterCheck: (entryLineId: Guid, voidReason: string, signal?: AbortSignal): Promise<VoidRegisterCheckResult> =>
    request<VoidRegisterCheckResult>(`/api/demo/books/register/${encodeURIComponent(entryLineId)}/void-check`, {
      method: "POST",
      body: JSON.stringify({ voidReason }),
      signal,
    }),

  /** GET /api/demo/books/register/unprinted-checks - what "Print unprinted checks" would print. */
  getUnprintedChecks: (registerAccountId: Guid, signal?: AbortSignal): Promise<UnprintedChecks> =>
    request<UnprintedChecks>(
      `/api/demo/books/register/unprinted-checks?registerAccountId=${encodeURIComponent(registerAccountId)}`,
      { signal },
    ),

  /** POST /api/demo/books/register/print-unprinted-checks - one transaction, date order; a rerun prints 0. */
  printUnprintedChecks: (registerAccountId: Guid, signal?: AbortSignal): Promise<PrintUnprintedChecksResult> =>
    request<PrintUnprintedChecksResult>("/api/demo/books/register/print-unprinted-checks", {
      method: "POST",
      body: JSON.stringify({ registerAccountId }),
      signal,
    }),

  /** POST /api/demo/places. A 409 means a place with that name already exists — compared
   *  without regard to case, and against archived places too (PlaceEndpoints.cs). */
  createPlace: (body: CreatePlaceRequest, signal?: AbortSignal): Promise<Place> =>
    request<Place>("/api/demo/places", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/places/{id} — a partial update; omitted fields keep their current
   *  value. Same 409 as create when the new name collides with another place. */
  updatePlace: (id: Guid, body: UpdatePlaceRequest, signal?: AbortSignal): Promise<Place> =>
    request<Place>(`/api/demo/places/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/places/{id} — archives. The row stays visible, but only in the
   *  archived disclosure, until `unarchivePlace` below brings it back. */
  archivePlace: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/places/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  /** POST /api/demo/places/{id}/unarchive (ef-038-3) — restores an archived place.
   *  Idempotent: a second call, or one against an already-active place, still succeeds. */
  unarchivePlace: (id: Guid, signal?: AbortSignal): Promise<Place> =>
    request<Place>(`/api/demo/places/${encodeURIComponent(id)}/unarchive`, {
      method: "POST",
      signal,
    }),

  // ---- ef-023: job codes — WHAT a day's hours were for, distinct from WHERE (places). ----
  // Owner-only server-side (403 for an employee login), same reasoning as the places
  // group above — an employee reaches a job code only indirectly, through the resolved
  // `allocations` on their own time entries, never through this group.

  /** GET /api/demo/job-codes — the farm's own job codes. `includeArchived` defaults to
   *  false server-side; this client always asks for both and lets `useJobCodes` split
   *  them, so the archived disclosure never needs a second round trip to open. */
  getJobCodes: (includeArchived = false, signal?: AbortSignal): Promise<JobCode[]> =>
    request<JobCode[]>(
      `/api/demo/job-codes${includeArchived ? "?includeArchived=true" : ""}`,
      { signal },
    ),

  /** POST /api/demo/job-codes. A 409 means a job code with that code already exists —
   *  compared without regard to case, and against archived job codes too
   *  (JobCodeEndpoints.cs). */
  createJobCode: (body: CreateJobCodeRequest, signal?: AbortSignal): Promise<JobCode> =>
    request<JobCode>("/api/demo/job-codes", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/job-codes/{id} — a partial update; omitted fields keep their
   *  current value. Same 409 as create when the new code collides with another. */
  updateJobCode: (id: Guid, body: UpdateJobCodeRequest, signal?: AbortSignal): Promise<JobCode> =>
    request<JobCode>(`/api/demo/job-codes/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/job-codes/{id} — archives, one-way. There is no restore route
   *  (JobCodeRepository.ArchiveAsync's own doc comment). UNLIKE Places.tsx's own
   *  archive: hours already allocated to this code keep it — see JobCodes.tsx's confirm
   *  copy, the opposite invariant from a place name. */
  archiveJobCode: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/job-codes/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  /** PUT /api/demo/time/{id}/allocations — the WHOLE split this call replaces (PUT
   *  semantics, never a merge). A 400 names either a malformed row or a sum that does not
   *  match the entry's own hours exactly (TimeEndpoints.cs). */
  putTimeAllocations: (
    id: Guid,
    body: PutAllocationsRequest,
    signal?: AbortSignal,
  ): Promise<Allocation[]> =>
    request<Allocation[]>(`/api/demo/time/${encodeURIComponent(id)}/allocations`, {
      method: "PUT",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-040-3: the owner's Schedule screen — /api/demo/schedule/shifts ----------
  // Owner-only server-side (403 for an employee login), same reasoning as the places/
  // job-codes groups above.

  /** GET /api/demo/schedule/shifts?from=&to=&employeeId= — a bounded window of shifts.
   *  `from`/`to` are REQUIRED server-side (ScheduleEndpoints.cs never defaults to
   *  everything); this client always sends both. */
  getShifts: (
    range: { from: string; to: string },
    employeeId?: Guid,
    signal?: AbortSignal,
  ): Promise<Shift[]> => {
    const params = new URLSearchParams({ from: range.from, to: range.to });
    if (employeeId) params.set("employeeId", employeeId);
    return request<Shift[]>(`/api/demo/schedule/shifts?${params.toString()}`, { signal });
  },

  /** POST /api/demo/schedule/shifts — always created `draft` server-side; publishing is
   *  a separate PATCH (see `updateShift` below). */
  createShift: (body: CreateShiftRequest, signal?: AbortSignal): Promise<Shift> =>
    request<Shift>("/api/demo/schedule/shifts", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/schedule/shifts/{id} — a partial update; omitted fields keep their
   *  current value. Also how a shift is published (`status: "published"`). */
  updateShift: (id: Guid, body: UpdateShiftRequest, signal?: AbortSignal): Promise<Shift> =>
    request<Shift>(`/api/demo/schedule/shifts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/schedule/shifts/{id} — a genuine removal, never a soft-delete
   *  (ef-040-2's own Assumption A1: no `archivedAt` column on `shift`). */
  deleteShift: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/schedule/shifts/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  // ---- ef-040-3: the owner's Time off screen — /api/demo/time-off ------------------
  // Owner-only server-side, same reasoning as the group above.

  /** GET /api/demo/time-off/pending — the review queue, oldest first. */
  getPendingTimeOff: (signal?: AbortSignal): Promise<TimeOffRequest[]> =>
    request<TimeOffRequest[]>("/api/demo/time-off/pending", { signal }),

  /** GET /api/demo/time-off?status=&from=&to= — decided (and pending) requests. Omit
   *  `status`/`range` for the server's own default window (the 90 days ending today,
   *  windowed on when the request was FILED) rather than one this client invents. */
  getTimeOffHistory: (
    status?: TimeOffStatus,
    range?: { from: string; to: string },
    signal?: AbortSignal,
  ): Promise<TimeOffRequest[]> => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (range) {
      params.set("from", range.from);
      params.set("to", range.to);
    }
    const qs = params.toString();
    return request<TimeOffRequest[]>(`/api/demo/time-off${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/time-off/{id}/review — a 409 means somebody already decided this
   *  row; it is not retryable, and the queue should be reloaded rather than retried
   *  (same discipline as `reviewTime` above). */
  reviewTimeOff: (id: Guid, body: ReviewTimeOffRequest, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/time-off/${encodeURIComponent(id)}/review`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-042-4: the owner's recurring-transaction templates — /api/demo/books/recurring/*
  // Owner-only server-side (403 for an employee login), same reasoning as the group above.

  /** GET /api/demo/books/recurring?includeInactive= — active templates only unless
   *  `includeInactive` is true, same shape as `getPlaces`/`getHerds`. */
  getRecurring: (includeInactive = false, signal?: AbortSignal): Promise<RecurringTemplate[]> => {
    const params = new URLSearchParams();
    if (includeInactive) params.set("includeInactive", "true");
    const qs = params.toString();
    return request<RecurringTemplate[]>(`/api/demo/books/recurring${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/books/recurring — `accountId` must name a category (non-Asset)
   *  account; an instrument is refused with a 400 naming `accountId`. */
  createRecurring: (body: CreateRecurringRequest, signal?: AbortSignal): Promise<RecurringTemplate> =>
    request<RecurringTemplate>("/api/demo/books/recurring", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/books/recurring/{id} — a partial update; omitted fields keep their
   *  current value. Also how a template is paused/resumed (`isActive: false/true`). */
  updateRecurring: (
    id: Guid,
    body: UpdateRecurringRequest,
    signal?: AbortSignal,
  ): Promise<RecurringTemplate> =>
    request<RecurringTemplate>(`/api/demo/books/recurring/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/books/recurring/{id} — a genuine removal; entries this template
   *  already posted are untouched (no FK back to this row). */
  deleteRecurring: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/books/recurring/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  /** POST /api/demo/books/recurring/{id}/post-now — NOT idempotent: a second back-to-back
   *  call posts a SECOND entry rather than refusing or deduping (BooksRecurringEndpoints.cs's
   *  own remarks). No due-date gate either — posts regardless of `nextDueOn`. */
  postRecurringNow: (id: Guid, signal?: AbortSignal): Promise<PostRecurringNowResponse> =>
    request<PostRecurringNowResponse>(`/api/demo/books/recurring/${encodeURIComponent(id)}/post-now`, {
      method: "POST",
      signal,
    }),

  // ---- ef-041-5: the owner's Livestock screen — /api/demo/livestock/* ---------------
  // Owner-only server-side (403 for an employee login), same reasoning as the group
  // above. Herds/groups are browsed only here (no create/update/delete client) — this
  // screen's own file scope is animal CRUD + status + the new history read.

  /** GET /api/demo/livestock/herds?entityId=&includeArchived= — a farm-scoped table, not
   *  paginated (LivestockEndpoints.cs's own judgment: "a handful of herds per farm").
   *  `includeArchived` defaults to false server-side, same shape as `getPlaces`. */
  getHerds: (entityId?: Guid, includeArchived = false, signal?: AbortSignal): Promise<Herd[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    if (includeArchived) params.set("includeArchived", "true");
    const qs = params.toString();
    return request<Herd[]>(`/api/demo/livestock/herds${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/livestock/herds/{id}/archive — ef-042-0b. NOT idempotent: a herd
   *  that is already archived (or does not resolve in this tenant) answers 404 — see
   *  `db/migrations/0043_herd_archive.sql`'s own header for why this differs from
   *  `archivePlace`'s idempotent DELETE shape. */
  archiveHerd: (id: Guid, signal?: AbortSignal): Promise<Herd> =>
    request<Herd>(`/api/demo/livestock/herds/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/livestock/herds/{id}/restore — ef-042-0b, the other direction.
   *  Idempotent: a second call, or one against an already-active herd, still succeeds. */
  restoreHerd: (id: Guid, signal?: AbortSignal): Promise<Herd> =>
    request<Herd>(`/api/demo/livestock/herds/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      signal,
    }),

  /** GET /api/demo/livestock/groups?entityId=&herdId= — same "small, farm-scoped table"
   *  judgment as `getHerds`, so not paginated. */
  getAnimalGroups: (entityId?: Guid, herdId?: Guid, signal?: AbortSignal): Promise<AnimalGroup[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    if (herdId) params.set("herdId", herdId);
    const qs = params.toString();
    return request<AnimalGroup[]>(`/api/demo/livestock/groups${qs ? `?${qs}` : ""}`, { signal });
  },

  /** GET /api/demo/livestock/animals?herdId=&groupId=&status=&limit=&offset= — the
   *  roster. BOUNDED AT BOTH ENDS server-side (LivestockEndpoints.MaxAnimalsPageSize,
   *  200); this client always sends an explicit `limit` rather than relying on the
   *  server's own smaller default (50), so the browsing screen sees the whole roster in
   *  one page up to that ceiling — see useLivestock.ts's own remarks on what happens
   *  past it. */
  getAnimals: (
    params: { herdId?: Guid; groupId?: Guid; status?: AnimalStatus; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<Animal[]> => {
    const qs = new URLSearchParams();
    if (params.herdId) qs.set("herdId", params.herdId);
    if (params.groupId) qs.set("groupId", params.groupId);
    if (params.status) qs.set("status", params.status);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<Animal[]>(`/api/demo/livestock/animals${query ? `?${query}` : ""}`, { signal });
  },

  /** POST /api/demo/livestock/animals — always created `active` server-side
   *  (`animal.status`'s own default). */
  createAnimal: (body: CreateAnimalRequest, signal?: AbortSignal): Promise<Animal> =>
    request<Animal>("/api/demo/livestock/animals", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/livestock/animals/{id} — a partial update; omitted fields keep
   *  their current value. Never carries `status` — see `UpdateAnimalRequest`'s own
   *  remarks. */
  updateAnimal: (id: Guid, body: UpdateAnimalRequest, signal?: AbortSignal): Promise<Animal> =>
    request<Animal>(`/api/demo/livestock/animals/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** DELETE /api/demo/livestock/animals/{id} — a genuine, hard delete (LivestockRepository's
   *  own doc comment). A second delete of the same, now-gone id is a plain 404. */
  deleteAnimal: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/livestock/animals/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  /** POST /api/demo/livestock/animals/{id}/status — writes the live column and the audit
   *  row atomically. A 409 means the animal is already in that status; it is refused, not
   *  silently re-logged — Livestock.tsx reads that case off `ApiError.status` directly
   *  rather than through `problemMessage`, so it can name the animal's OWN status back
   *  ("Already active.") instead of the server's generic sentence. */
  changeAnimalStatus: (id: Guid, body: ChangeAnimalStatusRequest, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/livestock/animals/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/livestock/animals/{id}/status-history — ef-041-5's own single API
   *  addition, most-recent-first (LivestockHistoryEndpoints.cs). */
  getAnimalStatusHistory: (id: Guid, signal?: AbortSignal): Promise<AnimalStatusChange[]> =>
    request<AnimalStatusChange[]>(`/api/demo/livestock/animals/${encodeURIComponent(id)}/status-history`, {
      signal,
    }),

  // ---- ef-059 anchor: dated weights, bulk entry, calf crop, head count ---------------

  /** GET /api/demo/livestock/animals/{id}/weights — newest `weighedOn` first (ef-059-3). */
  getAnimalWeights: (id: Guid, signal?: AbortSignal): Promise<AnimalWeight[]> =>
    request<AnimalWeight[]>(`/api/demo/livestock/animals/${encodeURIComponent(id)}/weights`, { signal }),

  /** POST /api/demo/livestock/animals/{id}/weights — a second weighing on the same day is a
   *  409; a future date or one before the birth date is a 400 naming `weighedOn`. */
  addAnimalWeight: (id: Guid, body: AddAnimalWeightRequest, signal?: AbortSignal): Promise<AnimalWeight> =>
    request<AnimalWeight>(`/api/demo/livestock/animals/${encodeURIComponent(id)}/weights`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/livestock/animals/bulk — all or nothing (ef-059-4). `idempotencyKey`: one
   *  per dialog open, so a double-clicked Save replays instead of posting twice. */
  createAnimalsBulk: (
    body: BulkCreateAnimalsRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<BulkCreateAnimalsResponse> =>
    request<BulkCreateAnimalsResponse>("/api/demo/livestock/animals/bulk", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /** POST /api/demo/livestock/calf-crop — one dam, one day, 1 to 4 calves (ef-059-4). */
  createCalfCrop: (
    body: CalfCropRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<CalfCropResponse> =>
    request<CalfCropResponse>("/api/demo/livestock/calf-crop", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /** GET /api/demo/livestock/headcount — exactly one of `herdId`/`groupId`; `asOf`
   *  (`YYYY-MM-DD`) omitted = today (ef-059-5). */
  getLivestockHeadcount: (
    params: { herdId?: Guid; groupId?: Guid; asOf?: string },
    signal?: AbortSignal,
  ): Promise<LivestockHeadcount> => {
    const qs = new URLSearchParams();
    if (params.herdId) qs.set("herdId", params.herdId);
    if (params.groupId) qs.set("groupId", params.groupId);
    if (params.asOf) qs.set("asOf", params.asOf);
    return request<LivestockHeadcount>(`/api/demo/livestock/headcount?${qs.toString()}`, { signal });
  },

  // ---- ef-061 anchor: Home tiles, equipment, reminders -------------------------------------

  /** GET /api/demo/home/layout — the caller's tiles in order (permission-filtered) + what they could add. */
  getHomeLayout: (signal?: AbortSignal): Promise<HomeLayout> =>
    request<HomeLayout>("/api/demo/home/layout", { signal }),

  /** PUT /api/demo/home/layout — saves the whole ordered list; unknown ids are dropped, not refused. */
  saveHomeLayout: (tiles: string[], signal?: AbortSignal): Promise<HomeLayout> =>
    request<HomeLayout>("/api/demo/home/layout", { method: "PUT", body: JSON.stringify({ tiles }), signal }),

  /** DELETE /api/demo/home/layout — back to the default set; answers 200 with that layout. */
  resetHomeLayout: (signal?: AbortSignal): Promise<HomeLayout> =>
    request<HomeLayout>("/api/demo/home/layout", { method: "DELETE", signal }),

  /** GET /api/demo/home/bills-due — unpaid bills due within 30 days and overdue ones (the Home tile). */
  getBillsDue: (entityId?: Guid, signal?: AbortSignal): Promise<BillsDue> =>
    request<BillsDue>(`/api/demo/home/bills-due${entityId ? `?entityId=${encodeURIComponent(entityId)}` : ""}`, {
      signal,
    }),

  /** GET /api/demo/equipment — archived equipment is hidden unless `includeArchived`. */
  listEquipment: (includeArchived = false, signal?: AbortSignal): Promise<Equipment[]> =>
    request<Equipment[]>(`/api/demo/equipment${includeArchived ? "?includeArchived=true" : ""}`, { signal }),

  /** POST /api/demo/equipment — a duplicate active name is a 409. */
  createEquipment: (body: CreateEquipmentRequest, signal?: AbortSignal): Promise<Equipment> =>
    request<Equipment>("/api/demo/equipment", { method: "POST", body: JSON.stringify(body), signal }),

  updateEquipment: (id: Guid, body: UpdateEquipmentRequest, signal?: AbortSignal): Promise<Equipment> =>
    request<Equipment>(`/api/demo/equipment/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  archiveEquipment: (id: Guid, signal?: AbortSignal): Promise<Equipment> =>
    request<Equipment>(`/api/demo/equipment/${encodeURIComponent(id)}/archive`, { method: "POST", signal }),

  restoreEquipment: (id: Guid, signal?: AbortSignal): Promise<Equipment> =>
    request<Equipment>(`/api/demo/equipment/${encodeURIComponent(id)}/restore`, { method: "POST", signal }),

  /** POST /api/demo/equipment/{id}/hours — a manual meter reading; lower/higher-than-neighbour needs a note (400 `note`). */
  logEquipmentHours: (id: Guid, body: LogHoursRequest, signal?: AbortSignal): Promise<HoursReading> =>
    request<HoursReading>(`/api/demo/equipment/${encodeURIComponent(id)}/hours`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/equipment/{id}/hours — newest reading first. */
  listEquipmentHours: (id: Guid, limit?: number, signal?: AbortSignal): Promise<HoursReading[]> =>
    request<HoursReading[]>(`/api/demo/equipment/${encodeURIComponent(id)}/hours${limit ? `?limit=${limit}` : ""}`, {
      signal,
    }),

  /** DELETE /api/demo/equipment/{id}/hours/{readingId} — 204. */
  deleteEquipmentHours: (id: Guid, readingId: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(
      `/api/demo/equipment/${encodeURIComponent(id)}/hours/${encodeURIComponent(readingId)}`,
      { method: "DELETE", signal },
    ),

  /** GET /api/demo/reminders — open reminders (most urgent first); `includeDone` adds completed one-time ones. */
  listReminders: (
    opts: { equipmentId?: Guid; includeDone?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<Reminder[]> => {
    const qs = new URLSearchParams();
    if (opts.equipmentId) qs.set("equipmentId", opts.equipmentId);
    if (opts.includeDone) qs.set("includeDone", "true");
    const q = qs.toString();
    return request<Reminder[]>(`/api/demo/reminders${q ? `?${q}` : ""}`, { signal });
  },

  /** GET /api/demo/reminders/due-soon — the Home tile's read: counts over all, the first `limit` items. */
  getDueSoonReminders: (limit?: number, signal?: AbortSignal): Promise<DueSoonReminders> =>
    request<DueSoonReminders>(`/api/demo/reminders/due-soon${limit ? `?limit=${limit}` : ""}`, { signal }),

  createReminder: (body: CreateReminderRequest, signal?: AbortSignal): Promise<Reminder> =>
    request<Reminder>("/api/demo/reminders", { method: "POST", body: JSON.stringify(body), signal }),

  updateReminder: (id: Guid, body: UpdateReminderRequest, signal?: AbortSignal): Promise<Reminder> =>
    request<Reminder>(`/api/demo/reminders/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/reminders/{id}/complete — logs the service and re-arms a repeating reminder. */
  completeReminder: (id: Guid, body: CompleteReminderRequest, signal?: AbortSignal): Promise<Reminder> =>
    request<Reminder>(`/api/demo/reminders/${encodeURIComponent(id)}/complete`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  archiveReminder: (id: Guid, signal?: AbortSignal): Promise<Reminder> =>
    request<Reminder>(`/api/demo/reminders/${encodeURIComponent(id)}/archive`, { method: "POST", signal }),

  // ---- ef-060 anchor: piece pay ---------------------------------------------------------

  /** GET /api/demo/piece-types — archived types are hidden unless `includeArchived` (ef-060-2). */
  listPieceTypes: (includeArchived = false, signal?: AbortSignal): Promise<PieceType[]> =>
    request<PieceType[]>(`/api/demo/piece-types${includeArchived ? "?includeArchived=true" : ""}`, { signal }),

  /** POST /api/demo/piece-types — a duplicate active name is a 409; rate must be > 0. */
  createPieceType: (body: CreatePieceTypeRequest, signal?: AbortSignal): Promise<PieceType> =>
    request<PieceType>("/api/demo/piece-types", { method: "POST", body: JSON.stringify(body), signal }),

  /** PATCH /api/demo/piece-types/{id} — a rate edit affects future tickets only. */
  updatePieceType: (id: Guid, body: UpdatePieceTypeRequest, signal?: AbortSignal): Promise<PieceType> =>
    request<PieceType>(`/api/demo/piece-types/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/piece-types/{id}/archive — hides the type from pickers; 409 when already archived. */
  archivePieceType: (id: Guid, signal?: AbortSignal): Promise<PieceType> =>
    request<PieceType>(`/api/demo/piece-types/${encodeURIComponent(id)}/archive`, { method: "POST", signal }),

  /** POST /api/demo/piece-types/{id}/restore — 409 when not archived. */
  restorePieceType: (id: Guid, signal?: AbortSignal): Promise<PieceType> =>
    request<PieceType>(`/api/demo/piece-types/${encodeURIComponent(id)}/restore`, { method: "POST", signal }),

  /** GET /api/demo/piece-tickets — newest first (ef-060-3). */
  listPieceTickets: (query: PieceTicketQuery = {}, signal?: AbortSignal): Promise<PieceTicket[]> => {
    const qs = new URLSearchParams();
    if (query.employeeId) qs.set("employeeId", query.employeeId);
    if (query.status) qs.set("status", query.status);
    if (query.limit) qs.set("limit", String(query.limit));
    const suffix = qs.toString();
    return request<PieceTicket[]>(`/api/demo/piece-tickets${suffix ? `?${suffix}` : ""}`, { signal });
  },

  /** POST /api/demo/piece-tickets — `idempotencyKey`: one per dialog open, so a double-clicked Save
   *  replays instead of posting twice. */
  createPieceTicket: (
    body: CreatePieceTicketRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<PieceTicket> =>
    request<PieceTicket>("/api/demo/piece-tickets", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /** PATCH /api/demo/piece-tickets/{id} — only while the ticket is open (409 once paid or void). */
  updatePieceTicket: (id: Guid, body: UpdatePieceTicketRequest, signal?: AbortSignal): Promise<PieceTicket> =>
    request<PieceTicket>(`/api/demo/piece-tickets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/piece-tickets/{id}/void — reason 1-200 characters; the row is kept. */
  voidPieceTicket: (id: Guid, reason: string, signal?: AbortSignal): Promise<PieceTicket> =>
    request<PieceTicket>(`/api/demo/piece-tickets/${encodeURIComponent(id)}/void`, {
      method: "POST",
      body: JSON.stringify({ reason }),
      signal,
    }),

  /** GET /api/demo/payroll/runs/{id}/piece — the piece tickets behind a pay run (ef-060-5). */
  getPayRunPiece: (runId: Guid, signal?: AbortSignal): Promise<PieceRunSummary> =>
    request<PieceRunSummary>(`/api/demo/payroll/runs/${encodeURIComponent(runId)}/piece`, { signal }),

  // ---- ef-041-6: Buy / Sell on the Livestock screen — one ledger entry per batch -----

  /** POST /api/demo/livestock/purchase — one balanced `entry` for the WHOLE batch
   *  (`LivestockRepository.PostPurchaseAsync`), asset-side, structurally excluded from the
   *  activity feed (`GetRecentEntriesAsync`'s own WHERE clause). A 409 never happens here;
   *  a missing chart-of-accounts entry (1550) or a bad line surfaces as a validation
   *  problem instead — `LivestockBatchDialog.tsx` reads that off `problemMessage`.
   *
   *  `idempotencyKey` (ef-048-2b) — see `voidDocument`'s own remarks. */
  postLivestockPurchase: (
    body: CreatePurchaseRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<LivestockPostingResponse> =>
    request<LivestockPostingResponse>("/api/demo/livestock/purchase", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  /** POST /api/demo/livestock/sale — one balanced `entry` for the WHOLE batch, income-side,
   *  visible in the activity feed as source "Livestock". A 409 means one or more named
   *  animals are no longer `active` (raced by another tab/device) — the whole batch is
   *  refused, nothing posted; `LivestockBatchDialog.tsx` reads that off `ApiError.status`
   *  directly, same convention `Livestock.tsx`'s own `changeAnimalStatus` uses.
   *
   *  `idempotencyKey` (ef-048-2b) — see `voidDocument`'s own remarks. */
  postLivestockSale: (
    body: CreateSaleRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<LivestockPostingResponse> =>
    request<LivestockPostingResponse>("/api/demo/livestock/sale", {
      method: "POST",
      body: JSON.stringify(body),
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
      signal,
    }),

  // ---- ef-045-3: fields ------------------------------------------------------------
  // Owner-only server-side (403 for an employee login), same posture as the Livestock
  // group above — this wave has no employee-facing field route either
  // (`FieldEndpoints.cs`'s own remarks). NO archive/restore route exists on either
  // `field` or `field_crop` this wave — `types.ts`'s own header comment for this
  // section — so none is called here; flagged in this job's own return contract.

  /** GET /api/demo/farm/fields?entityId= — a farm-scoped table, not paginated (same
   *  judgment `FieldEndpoints.cs` makes as `LivestockEndpoints`'s own herds/groups). */
  getFields: (entityId?: Guid, signal?: AbortSignal): Promise<Field[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    const qs = params.toString();
    return request<Field[]>(`/api/demo/farm/fields${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/fields. */
  createField: (body: CreateFieldRequest, signal?: AbortSignal): Promise<Field> =>
    request<Field>("/api/demo/farm/fields", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/farm/fields/{id} — a partial update; omitted fields keep their
   *  current value (`FieldRepository.UpdateFieldAsync`'s COALESCE update). Not called
   *  from Fields.tsx this wave (no edit affordance in the brief) but wired for parity
   *  with the route's own existence, same posture `updateAnimal` takes beside `Animal`. */
  updateField: (id: Guid, body: UpdateFieldRequest, signal?: AbortSignal): Promise<Field> =>
    request<Field>(`/api/demo/farm/fields/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/farm/field-crops?fieldId=&year= — crop-year assignments. Fields.tsx
   *  calls this with `year` alone (never `fieldId`) so one request covers every field's
   *  assignments for the selected crop year, grouped client-side by `fieldId`. */
  getFieldCrops: (
    params: { fieldId?: Guid; year?: number } = {},
    signal?: AbortSignal,
  ): Promise<FieldCrop[]> => {
    const qs = new URLSearchParams();
    if (params.fieldId) qs.set("fieldId", params.fieldId);
    if (params.year != null) qs.set("year", String(params.year));
    const query = qs.toString();
    return request<FieldCrop[]>(`/api/demo/farm/field-crops${query ? `?${query}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/field-crops — a 409 means the exact same (field, year, crop) is
   *  already recorded; a DIFFERENT crop the same field+year is double-cropping and is
   *  accepted (`FieldEndpoints.CropYearConflict`). Fields.tsx reads that case off
   *  `ApiError.status` directly, same convention `Livestock.tsx`'s own
   *  `changeAnimalStatus` uses for its own 409. */
  createFieldCrop: (body: CreateFieldCropRequest, signal?: AbortSignal): Promise<FieldCrop> =>
    request<FieldCrop>("/api/demo/farm/field-crops", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/farm/field-crops/{id} — only `acres` is patchable (see
   *  `UpdateFieldCropRequest`'s own remarks). Wired for parity with the route, not
   *  called from Fields.tsx this wave. */
  updateFieldCrop: (id: Guid, body: UpdateFieldCropRequest, signal?: AbortSignal): Promise<FieldCrop> =>
    request<FieldCrop>(`/api/demo/farm/field-crops/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-045-4: applications ------------------------------------------------------
  // Owner-only server-side (403 for an employee login), same posture as the
  // `ef-045-3: fields` group above — `FieldApplicationEndpoints.cs`'s own remarks. Add-only
  // this wave — no PATCH/DELETE route exists, so neither is wired here.

  /** GET /api/demo/farm/field-crops/{id}/applications — newest `appliedOn` first
   *  (`FieldApplicationRepository.GetApplicationsAsync`'s own `ORDER BY`); Fields.tsx
   *  renders the response as-is, never re-sorting client-side. */
  getFieldApplications: (fieldCropId: Guid, signal?: AbortSignal): Promise<FieldCropApplication[]> =>
    request<FieldCropApplication[]>(
      `/api/demo/farm/field-crops/${encodeURIComponent(fieldCropId)}/applications`,
      { signal },
    ),

  /** POST /api/demo/farm/field-crops/{id}/applications — `fieldCropId` must resolve on
   *  this tenant; a cross-tenant or nonexistent id is a 404, never a validation problem
   *  (`FieldApplicationEndpoints.cs`'s own remarks, same rule `createFieldCrop` above
   *  follows for its own `fieldId`). */
  createFieldApplication: (
    fieldCropId: Guid,
    body: CreateFieldApplicationRequest,
    signal?: AbortSignal,
  ): Promise<FieldCropApplication> =>
    request<FieldCropApplication>(
      `/api/demo/farm/field-crops/${encodeURIComponent(fieldCropId)}/applications`,
      { method: "POST", body: JSON.stringify(body), signal },
    ),

  // ---- ef-045-5: harvest ------------------------------------------------------------
  // POST/GET `/api/demo/farm/field-crops/{id}/harvest(s)` — ef-045-5a's routes
  // (`FieldHarvestEndpoints.cs`/`FieldHarvestContracts.cs`). Owner-only server-side, same
  // posture as the `ef-045-4: applications` group above. Add-only this wave — no PATCH/
  // DELETE route exists, so neither is wired here.

  /** GET /api/demo/farm/field-crops/{id}/harvests — newest `harvestedOn` first
   *  (`FieldHarvestRepository.GetHarvestsAsync`'s own `ORDER BY`); Fields.tsx reads
   *  `[0]` as the most recent harvest and never re-sorts client-side. */
  getFieldHarvests: (fieldCropId: Guid, signal?: AbortSignal): Promise<FieldCropHarvest[]> =>
    request<FieldCropHarvest[]>(
      `/api/demo/farm/field-crops/${encodeURIComponent(fieldCropId)}/harvests`,
      { signal },
    ),

  /** POST /api/demo/farm/field-crops/{id}/harvest — records the harvest AND, in the
   *  same write, posts its own crop sale (`source = 'crop'`, `4010 Grain Sales`). No
   *  separate "sell" route exists this wave — this POST IS the sale
   *  (`FieldHarvestEndpoints.cs`'s own remarks). `fieldCropId` must resolve on this
   *  tenant; a cross-tenant or nonexistent id is a 404, same rule
   *  `createFieldApplication` above follows for its own `fieldCropId`. */
  postFieldHarvest: (
    fieldCropId: Guid,
    body: CreateFieldHarvestRequest,
    signal?: AbortSignal,
  ): Promise<FieldCropHarvest> =>
    request<FieldCropHarvest>(
      `/api/demo/farm/field-crops/${encodeURIComponent(fieldCropId)}/harvest`,
      { method: "POST", body: JSON.stringify(body), signal },
    ),

  // ---- ef-046-5: overview ----

  /** GET /api/demo/farm/overview?entityId= — the Farm tile's Overview tab. Omitted
   *  `entityId` covers the whole tenant (`FarmOverviewEndpoints.cs`'s own remarks); Farm.tsx
   *  never passes one, same "whole farm" scope `farm-overview.spec.ts` asserts against. */
  getFarmOverview: (entityId?: Guid, signal?: AbortSignal): Promise<FarmOverview> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    const qs = params.toString();
    return request<FarmOverview>(`/api/demo/farm/overview${qs ? `?${qs}` : ""}`, { signal });
  },
  // ---- ef-046-1: bins ----------------------------------------------------------------
  // GET/POST `/api/demo/farm/grain-bins` — ef-046-1b's routes (`GrainBinEndpoints.cs`/
  // `GrainBinContracts.cs`). Owner-only server-side, same posture as the `ef-045-5:
  // harvest` group above. The harvest form's own bin picker (ef-046-1c) is the only
  // caller of `listGrainBins` this wave; `createGrainBin` is wired ahead of the bin
  // management screen (ef-046-4) that will call it.

  /** GET /api/demo/farm/grain-bins — bins for the entity, archived ones already excluded
   *  server-side (`GrainBinRepository.GetBinsAsync`'s own `WHERE archived_at IS NULL`),
   *  ordered by name. Same optional-`entityId`-querystring shape as `getFields` above. */
  listGrainBins: (entityId?: Guid, signal?: AbortSignal): Promise<GrainBin[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    const qs = params.toString();
    return request<GrainBin[]>(`/api/demo/farm/grain-bins${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/grain-bins. */
  createGrainBin: (body: CreateGrainBinRequest, signal?: AbortSignal): Promise<GrainBin> =>
    request<GrainBin>("/api/demo/farm/grain-bins", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-063 anchors: elevator storage fee + scale loads -----------------------------------

  /** POST /api/demo/farm/grain-bins/{id}/storage-fee — posts ONE bill to the elevator's vendor (a `document`, kind
   *  `bill`; no ledger entry until it is settled). 400: not an elevator bin / bad amount / the account is not an
   *  expense account; 404: the bin is not this tenant's. */
  createGrainStorageFee: (
    binId: Guid,
    body: CreateGrainStorageFeeRequest,
    signal?: AbortSignal,
  ): Promise<GrainStorageFee> =>
    request<GrainStorageFee>(`/api/demo/farm/grain-bins/${encodeURIComponent(binId)}/storage-fee`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** GET /api/demo/farm/grain-loads?entityId=&limit=&offset= — newest weighed first; `limit` 1..200 (default 50). */
  listGrainLoads: (
    params: { entityId?: Guid; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<GrainLoad[]> => {
    const qs = new URLSearchParams();
    if (params.entityId) qs.set("entityId", params.entityId);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return request<GrainLoad[]>(`/api/demo/farm/grain-loads${query ? `?${query}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/grain-loads — records one scale ticket (source `manual`) and books its movement. 400: a
   *  rule broken (net != gross - tare, no / two destinations, a unit that disagrees with the bin or contract);
   *  404: a field crop / bin / contract that is not this tenant's; 409: the ticket is already recorded. */
  createGrainLoad: (body: CreateGrainLoadRequest, signal?: AbortSignal): Promise<GrainLoad> =>
    request<GrainLoad>("/api/demo/farm/grain-loads", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/farm/grain-loads/import?entityId=&source= — the canonical CSV (`text/csv`) or JSON
   *  (`application/json`, an array) import. Idempotent on (source, ticket); a 400 carries `rowErrors` and imports
   *  nothing. No screen calls this yet (the vendor mapper UI is ef-064). */
  importGrainLoads: (
    params: { entityId: Guid; source: string },
    body: string,
    contentType: "text/csv" | "application/json",
    signal?: AbortSignal,
  ): Promise<GrainLoadImportResult> =>
    request<GrainLoadImportResult>(
      `/api/demo/farm/grain-loads/import?entityId=${encodeURIComponent(params.entityId)}&source=${encodeURIComponent(params.source)}`,
      { method: "POST", body, headers: { "Content-Type": contentType }, signal },
    ),

  // ---- ef-064-12..15 anchors: the scale-ticket import screen ------------------------------------------------------

  /** POST /api/demo/farm/grain-loads/import?entityId=&format=&source= - ef-064-14. `format: "agscale"` reads Christy's Ag Scale
   *  ticket app (her bulk JSON or her CSV export; `source` optional, default `agscale`); `"canonical"` is the import above
   *  (`source` required). Content type follows the file: `.csv` -> `text/csv`, anything else -> `application/json`. A refusal
   *  throws an ApiError - read it with `importRefusal`. */
  importScaleTickets: (
    params: { entityId: Guid; format: GrainLoadImportFormat; source?: string },
    body: string,
    contentType: "text/csv" | "application/json",
    signal?: AbortSignal,
  ): Promise<GrainLoadImportResult> => {
    const qs = new URLSearchParams({ entityId: params.entityId, format: params.format });
    if (params.source !== undefined && params.source.trim() !== "") qs.set("source", params.source.trim());
    return request<GrainLoadImportResult>(`/api/demo/farm/grain-loads/import?${qs.toString()}`, {
      method: "POST",
      body,
      headers: { "Content-Type": contentType },
      signal,
    });
  },

  /** GET /api/demo/farm/scale-aliases?source=&kind= - ef-064-12 (owner only). */
  listScaleAliases: (query: { source?: string; kind?: ScaleAliasKind } = {}, signal?: AbortSignal): Promise<ScaleAlias[]> => {
    const qs = new URLSearchParams();
    if (query.source) qs.set("source", query.source);
    if (query.kind) qs.set("kind", query.kind);
    const q = qs.toString();
    return request<ScaleAlias[]>(`/api/demo/farm/scale-aliases${q ? `?${q}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/scale-aliases - 201; 400 a bad field, 404 an unknown / archived / other-kind target, 409 the name is
   *  already mapped for that source + kind. */
  createScaleAlias: (body: CreateScaleAliasRequest, signal?: AbortSignal): Promise<ScaleAlias> =>
    request<ScaleAlias>("/api/demo/farm/scale-aliases", { method: "POST", body: JSON.stringify(body), signal }),

  /** POST /api/demo/farm/scale-aliases/batch - 200 all-or-nothing: a name already mapped is retargeted, any other created; 400 a bad
   *  item, 404 an unknown target, 409 two items read the same, one owner-data permit per call. */
  createScaleAliasBatch: (body: ScaleAliasBatchRequest, signal?: AbortSignal): Promise<ScaleAliasBatchResponse> =>
    request<ScaleAliasBatchResponse>("/api/demo/farm/scale-aliases/batch", { method: "POST", body: JSON.stringify(body), signal }),

  /** PUT /api/demo/farm/scale-aliases/{id} - point an alias at another row of the same kind. */
  updateScaleAlias: (id: Guid, targetId: Guid, signal?: AbortSignal): Promise<ScaleAlias> =>
    request<ScaleAlias>(`/api/demo/farm/scale-aliases/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ targetId }),
      signal,
    }),

  /** DELETE /api/demo/farm/scale-aliases/{id} - 204. */
  deleteScaleAlias: (id: Guid, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/demo/farm/scale-aliases/${encodeURIComponent(id)}`, { method: "DELETE", signal }),

  /** GET /api/demo/farm/commodities - ef-064-2's effective catalog (the scale-ticket fixer offers it for a commodity name). */
  getCommodities: (signal?: AbortSignal): Promise<Commodity[]> =>
    request<Commodity[]>("/api/demo/farm/commodities", { signal }),

  // ---- ef-046-4: contracts and deliveries ---------------------------------------------
  // POST/GET/PATCH `/api/demo/farm/grain-contracts`, POST
  // `/api/demo/farm/grain-deliveries` — ef-046-2/-3's routes
  // (`GrainContractEndpoints.cs`/`GrainDeliveryEndpoints.cs`). Owner-only server-side,
  // same posture as the `ef-046-1: bins` group above. `GrainContractResponse.status`
  // (open/partial/fulfilled) is derived server-side, never computed here.

  /** GET /api/demo/farm/grain-contracts?entityId= — a farm-scoped table, not paginated,
   *  same judgment as `getGrainBins` above. */
  getGrainContracts: (entityId?: Guid, signal?: AbortSignal): Promise<GrainContract[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    const qs = params.toString();
    return request<GrainContract[]>(`/api/demo/farm/grain-contracts${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/grain-contracts. A 404 (not a validation problem) means
   *  `buyerId` did not resolve to a vendor on this tenant — `GrainContractEndpoints.cs`'s
   *  own remarks, same divergence its own header documents from `counterpartyId`. */
  createGrainContract: (
    body: CreateGrainContractRequest,
    signal?: AbortSignal,
  ): Promise<GrainContract> =>
    request<GrainContract>("/api/demo/farm/grain-contracts", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/farm/grain-contracts/{id} — a partial update; an omitted field
   *  keeps its current value. Not called from Grain.tsx this wave (no edit affordance in
   *  the brief) but wired for parity with the route's own existence, same posture
   *  `updateField` takes beside `Field`. */
  updateGrainContract: (
    id: Guid,
    body: UpdateGrainContractRequest,
    signal?: AbortSignal,
  ): Promise<GrainContract> =>
    request<GrainContract>(`/api/demo/farm/grain-contracts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/farm/grain-deliveries — a 409 means the bin doesn't hold enough of
   *  the contract's own crop, or this tenant's chart of accounts is missing the Grain
   *  Sales account (`GrainDeliveryEndpoints.cs`'s own remarks); Grain.tsx reads that
   *  message off `problemMessage`, same convention `HarvestForm`'s own submit uses for
   *  its own 409s, rather than guessing the bin's own on-hand client-side. */
  createGrainDelivery: (
    body: CreateGrainDeliveryRequest,
    signal?: AbortSignal,
  ): Promise<GrainDelivery> =>
    request<GrainDelivery>("/api/demo/farm/grain-deliveries", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-049-2: milk tickets ----------------------------------------------------------
  // GET/POST `/api/demo/farm/milk-tickets` — ef-049-2's routes (`MilkTicketEndpoints.cs`/
  // `MilkTicketContracts.cs`). Owner-only server-side, same posture as the `ef-046-4:
  // contracts and deliveries` group above.

  /** GET /api/demo/farm/milk-tickets?entityId= — most-recent-first, already ordered
   *  server-side (`MilkTicketRepository.GetTicketsAsync`'s own `ORDER BY`). Same
   *  optional-`entityId`-querystring shape as `getGrainContracts` above. */
  listMilkTickets: (entityId?: Guid, signal?: AbortSignal): Promise<MilkTicket[]> => {
    const params = new URLSearchParams();
    if (entityId) params.set("entityId", entityId);
    const qs = params.toString();
    return request<MilkTicket[]>(`/api/demo/farm/milk-tickets${qs ? `?${qs}` : ""}`, { signal });
  },

  /** POST /api/demo/farm/milk-tickets — a 409 means this tenant's chart of accounts is
   *  missing the Milk Sales account (`MilkTicketEndpoints.cs`'s own remarks); Milk.tsx
   *  reads that message off `problemMessage`, same convention `RecordDeliveryDialog`'s
   *  own submit uses for its own 409s. */
  createMilkTicket: (
    body: CreateMilkTicketRequest,
    signal?: AbortSignal,
  ): Promise<MilkTicket> =>
    request<MilkTicket>("/api/demo/farm/milk-tickets", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-050-2: partner splits ---------------------------------------------------------
  // POST/GET/PATCH `/api/demo/farm/partner-splits` plus `.../archive`/`.../restore` —
  // ef-050-2's routes (`PartnerSplitEndpoints.cs`/`PartnerSplitContracts.cs`). Owner-only
  // server-side, same posture as the milk-tickets group above.

  /** GET /api/demo/farm/partner-splits?entityId= — active splits only, partner-name
   *  order, unless `includeArchived` asks for the full list. `entityId` is REQUIRED
   *  server-side (`PartnerSplitEndpoints.cs`'s own remarks) — never call this before
   *  `useSelectedEntity` resolves one. */
  listPartnerSplits: (
    entityId: Guid,
    includeArchived?: boolean,
    signal?: AbortSignal,
  ): Promise<PartnerSplit[]> => {
    const params = new URLSearchParams({ entityId });
    if (includeArchived) params.set("includeArchived", "true");
    return request<PartnerSplit[]>(`/api/demo/farm/partner-splits?${params.toString()}`, { signal });
  },

  /** POST /api/demo/farm/partner-splits — a 400 named `entityId` means it does not
   *  resolve on this tenant (`PartnerSplitEndpoints.cs`'s own remarks); `Farm.tsx` reads
   *  that off `problemFieldErrors`, same convention every other create form in this
   *  project uses for its own field errors. */
  createPartnerSplit: (
    body: CreatePartnerSplitRequest,
    signal?: AbortSignal,
  ): Promise<PartnerSplit> =>
    request<PartnerSplit>("/api/demo/farm/partner-splits", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/farm/partner-splits/{id} — a partial update; omitted fields keep
   *  their current value. */
  updatePartnerSplit: (
    id: Guid,
    body: UpdatePartnerSplitRequest,
    signal?: AbortSignal,
  ): Promise<PartnerSplit> =>
    request<PartnerSplit>(`/api/demo/farm/partner-splits/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/farm/partner-splits/{id}/archive — NOT idempotent: a second call
   *  against an already-archived split answers 404, same posture `archiveEntity` above
   *  already takes. */
  archivePartnerSplit: (id: Guid, signal?: AbortSignal): Promise<PartnerSplit> =>
    request<PartnerSplit>(`/api/demo/farm/partner-splits/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/farm/partner-splits/{id}/restore — idempotent: a second call, or one
   *  against an already-active split, still succeeds. */
  restorePartnerSplit: (id: Guid, signal?: AbortSignal): Promise<PartnerSplit> =>
    request<PartnerSplit>(`/api/demo/farm/partner-splits/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      signal,
    }),

  // ---- ef-047-2: entities — Setup's Logins tab. Owner-only server-side (403 for an
  // employee login), same reasoning as the accounts/places/job-codes groups above. ----

  /** GET /api/demo/entities?includeArchived=true — the WHOLE list, active and archived,
   *  in one round trip. Kept separate from `getEntities` above (which every other entity
   *  picker in the app calls with no argument and must keep reading active-only) rather
   *  than extending it, so no existing caller has to change — same shape
   *  `getAllAccountsIncludingArchived` uses for its own archived disclosure.
   *
   *  NOT 403 for a non-owner caller — unlike the write routes in this group, this GET is
   *  reachable by anyone (it's also the visitor picker's route). `includeArchived` is
   *  honoured only for an OWNER session; an employee cookie or a header-only caller gets
   *  the active list back silently, same shape as a plain GET, never an error
   *  (DemoEndpoints.cs, TryOpenSessionWithOwnerFlagAsync). Only call this from Access.tsx. */
  getAllEntitiesIncludingArchived: (signal?: AbortSignal): Promise<BusinessEntity[]> =>
    request<BusinessEntity[]>("/api/demo/entities?includeArchived=true", { signal }),

  /** POST /api/demo/entities. A 409 means an entity with that name already exists —
   *  compared against every other ACTIVE entity, and NOT against archived ones
   *  (EntityAdminEndpoints.cs). */
  createEntity: (body: CreateEntityRequest, signal?: AbortSignal): Promise<BusinessEntity> =>
    request<BusinessEntity>("/api/demo/entities", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** PATCH /api/demo/entities/{id} — rename only; see `UpdateEntityRequest`'s own
   *  remarks for why pay schedule is not offered here. */
  renameEntity: (
    id: Guid,
    body: UpdateEntityRequest,
    signal?: AbortSignal,
  ): Promise<BusinessEntity> =>
    request<BusinessEntity>(`/api/demo/entities/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/entities/{id}/archive — ef-047-2. Reversible (`restoreEntity`
   *  below); a second call against an already-archived entity is refused with 404, same
   *  posture as `archiveAccount`. Clears the tenant's default in the same write if this
   *  was it — the response's own `isDefault` reflects that. */
  archiveEntity: (id: Guid, signal?: AbortSignal): Promise<BusinessEntity> =>
    request<BusinessEntity>(`/api/demo/entities/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/entities/{id}/restore — the other direction. Idempotent: a second
   *  call, or one against an already-active entity, still succeeds. Never reclaims the
   *  default slot — see EntityRepository.RestoreAsync's own remarks. */
  restoreEntity: (id: Guid, signal?: AbortSignal): Promise<BusinessEntity> =>
    request<BusinessEntity>(`/api/demo/entities/${encodeURIComponent(id)}/restore`, {
      method: "POST",
      signal,
    }),

  /** POST /api/demo/entities/{id}/set-default — the tenant's one default entity. A 409
   *  means the target is archived (EntitySetDefaultOutcome.Archived) — restore it first. */
  setDefaultEntity: (id: Guid, signal?: AbortSignal): Promise<BusinessEntity> =>
    request<BusinessEntity>(`/api/demo/entities/${encodeURIComponent(id)}/set-default`, {
      method: "POST",
      signal,
    }),

  // ---- ef-047-3: pay schedule ----

  /** PATCH /api/demo/entities/{id}/pay-schedule — OWNER-ONLY (ef-015, DemoEndpoints.cs).
   *  `firstPayday` omitted/`null` clears the entity's own anchor back to
   *  `PayrollCalendar.FirstPaydayOf` — see `UpdatePayScheduleRequest`'s own remarks for why
   *  there is no partial-update path that changes cadence alone. A 400 carries
   *  field errors (`paySchedule`/`firstPayday`) read the same way `problemFieldErrors`
   *  reads every other route's. */
  updateEntityPaySchedule: (
    id: Guid,
    body: { paySchedule: string; firstPayday: string | null },
    signal?: AbortSignal,
  ): Promise<BusinessEntity> =>
    request<BusinessEntity>(`/api/demo/entities/${encodeURIComponent(id)}/pay-schedule`, {
      method: "PATCH",
      body: JSON.stringify(body),
      signal,
    }),

  // ---- ef-062-8 anchor: an entity's starting check number ----

  /** GET /api/demo/entities/{id}/next-check-number - OWNER-ONLY. The number the next check (payroll or register) will get. */
  getNextCheckNumber: (entityId: Guid, signal?: AbortSignal): Promise<NextCheckNumber> =>
    request<NextCheckNumber>(`/api/demo/entities/${encodeURIComponent(entityId)}/next-check-number`, { signal }),

  /** PUT /api/demo/entities/{id}/next-check-number - OWNER-ONLY. 400 field `nextCheckNumber`: missing, < 1, > 999,999,999,
   *  or not higher than the highest check number already on record (a printed or voided number is never reused). Idempotent. */
  setNextCheckNumber: (entityId: Guid, nextCheckNumber: number, signal?: AbortSignal): Promise<NextCheckNumber> =>
    request<NextCheckNumber>(`/api/demo/entities/${encodeURIComponent(entityId)}/next-check-number`, {
      method: "PUT",
      body: JSON.stringify({ nextCheckNumber }),
      signal,
    }),

  // ---- ef-048-5: Home's cash-flow planner ----

  /** GET /api/demo/home/cash-flow?weeks= — the Home dashboard's read-only cash-flow
   *  projection. Omitted `weeks` defaults server-side to 8
   *  (`CashFlowPlannerEndpoints.cs`); Dashboard.tsx never passes one. */
  getCashFlowPlanner: (weeks?: number, signal?: AbortSignal): Promise<CashFlowPlanner> =>
    request<CashFlowPlanner>(
      `/api/demo/home/cash-flow${weeks ? `?weeks=${weeks}` : ""}`,
      { signal },
    ),

  // ---- ef-051-1: Home memos ----
  // POST/GET/PATCH /api/demo/home/memos plus .../archive (`MemoEndpoints.cs`). No
  // restore route this slice — explicit scope cut, not a gap.

  /** GET /api/demo/home/memos?limit= — active memos only, dated-ascending then undated
   *  last, server-side (never re-sorted here). The Home card calls this with `limit=5` —
   *  a real server-side page, never a client-side slice of a larger response. */
  getMemos: (limit?: number, signal?: AbortSignal): Promise<Memo[]> =>
    request<Memo[]>(`/api/demo/home/memos${limit ? `?limit=${limit}` : ""}`, { signal }),

  /** POST /api/demo/home/memos — a 400 named `entityId` means it does not resolve on
   *  this tenant (`MemoEndpoints.cs`'s own remarks); the Home card's inline form never
   *  sends one. */
  createMemo: (body: CreateMemoRequest, signal?: AbortSignal): Promise<Memo> =>
    request<Memo>("/api/demo/home/memos", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),

  /** POST /api/demo/home/memos/{id}/archive — NOT idempotent: a second call against an
   *  already-archived memo answers 404, same posture `archivePartnerSplit` above already
   *  takes. */
  archiveMemo: (id: Guid, signal?: AbortSignal): Promise<Memo> =>
    request<Memo>(`/api/demo/home/memos/${encodeURIComponent(id)}/archive`, {
      method: "POST",
      signal,
    }),
};

/**
 * The sentence the SERVER wrote, pulled back out of an ApiError.
 *
 * `request()` above flattens a failure into `"409 Conflict: {json}"` — fine for a
 * console, useless on a phone. The time endpoints, unlike most of this API, answer with
 * genuinely user-facing prose ("You are already clocked in.", "Only the last 14 days can
 * be entered here.") and with per-field validation messages, and throwing that away to
 * show a generic "something went wrong" would hide the one thing that tells a worker
 * what to do next.
 *
 * Parsing the body back out of the message is deliberate rather than changing
 * `request()`: every other caller in this app already depends on that flattening, and a
 * narrow reader here cannot break them. It falls back to `fallback` on anything it does
 * not recognise, so a genuinely unexpected failure never renders as a fragment of JSON.
 */
export function problemMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  // A network failure never reached the server, so there is no server sentence to find.
  if (err.status === 0) return "No connection — this didn't reach the farm office.";

  const start = err.message.indexOf("{");
  if (start === -1) return fallback;

  try {
    const problem = JSON.parse(err.message.slice(start)) as {
      title?: string;
      detail?: string;
      errors?: Record<string, string[]>;
    };
    // Validation first: a per-field message is more specific than the generic title
    // ASP.NET puts on a ValidationProblem ("One or more validation errors occurred.").
    const firstFieldError = Object.values(problem.errors ?? {}).flat()[0];
    if (firstFieldError) return firstFieldError;

    const sentence = [problem.title, problem.detail].filter(Boolean).join(" ");
    return sentence || fallback;
  } catch {
    return fallback;
  }
}

/**
 * The raw field->messages map from a 400 ValidationProblem, or null when the error
 * carries none — a 409 conflict, a network failure, anything that is not a per-field
 * rejection. `problemMessage` above only ever surfaces the FIRST such message as a
 * sentence; Places.tsx needs the whole map, to mark the specific `<input>` a farmer needs
 * to fix (aria-invalid) rather than just reading a paragraph about it. Parses the same
 * flattened `err.message` `problemMessage` does, deliberately kept as its own small
 * parse rather than sharing state with it — the two callers want different shapes back
 * from the same body, and coupling them would mean a Places-only need forcing a change
 * to every other caller of `problemMessage`.
 */
export function problemFieldErrors(err: unknown): Record<string, string[]> | null {
  if (!(err instanceof ApiError)) return null;
  const start = err.message.indexOf("{");
  if (start === -1) return null;
  try {
    const problem = JSON.parse(err.message.slice(start)) as { errors?: Record<string, string[]> };
    return problem.errors ?? null;
  } catch {
    return null;
  }
}

/**
 * ef-064-14 anchor - a refused grain-load import, read back out of the flattened ApiError the same way `problemFieldErrors`
 * reads `errors`. Null when `err` is not a 400 carrying an import refusal (a 401/403/415/5xx, a network failure). A whole-file
 * problem (`errors.file`, or `errors.format` / `errors.source` / `errors.entityId`) comes back as `fileError`; row problems as
 * `rowErrors` (at most 50) and, for `format=agscale`, the names that matched nothing as `unmapped` (at most 50).
 */
export function importRefusal(err: unknown): GrainLoadImportRefusal | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const start = err.message.indexOf("{");
  if (start === -1) return null;
  try {
    const problem = JSON.parse(err.message.slice(start)) as {
      errors?: Record<string, string[]>;
      rowErrors?: GrainLoadRowError[];
      unmapped?: ScaleAliasUnmapped[];
    };
    const errs = problem.errors ?? {};
    const fileError = errs.file?.[0] ?? errs.format?.[0] ?? errs.source?.[0] ?? errs.entityId?.[0] ?? null;
    const rowErrors = Array.isArray(problem.rowErrors) ? problem.rowErrors : [];
    const unmapped = Array.isArray(problem.unmapped) ? problem.unmapped : [];
    if (fileError === null && rowErrors.length === 0 && unmapped.length === 0) return null;
    return { fileError, rowErrors, unmapped };
  } catch {
    return null;
  }
}

/**
 * ef-038-3 — the `archived`/`placeId` extension members `PlaceEndpoints.NameConflict`
 * adds to a place-creation 409, parsed the same way `problemFieldErrors` reads `errors`
 * back out of the same flattened `err.message`. Null for anything that is not that
 * specific shape (a different 409, a validation 400, a network failure) — callers must
 * not assume a truthy `archived` on a `null` return.
 */
export function placeNameConflict(err: unknown): { archived: boolean; placeId: Guid | null } | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const start = err.message.indexOf("{");
  if (start === -1) return null;
  try {
    const problem = JSON.parse(err.message.slice(start)) as {
      archived?: boolean;
      placeId?: Guid | null;
    };
    if (typeof problem.archived !== "boolean") return null;
    return { archived: problem.archived, placeId: problem.placeId ?? null };
  } catch {
    return null;
  }
}

/**
 * Raised for a 400 from /api/payroll/paycheck. Carries the field->messages map
 * from the API's ValidationProblem response rather than just a stringified
 * body, so a caller can show the SAME reason the server rejected the request
 * (e.g. "Social Security wage base for 2031 is not configured.") instead of a
 * generic "something went wrong."
 */
export class PayrollValidationError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super(Object.values(errors).flat().join(" ") || "The payroll engine rejected this request.");
    this.name = "PayrollValidationError";
  }
}

/**
 * POST /api/payroll/paycheck — the real, tested payroll engine
 * (EasyFarm.PayrollDeductions), not a demo fixture.
 *
 * Deliberately bypasses `request()` above: that helper requires
 * VITE_DEMO_TENANT and always sends `X-Demo-Tenant`, but this endpoint takes
 * no tenant header and is not RLS-scoped — it is a pure calculator. Sending a
 * tenant header here would be plumbing this route doesn't use and doesn't
 * need, and would make it silently depend on a build-time env var that has
 * nothing to do with what it computes.
 */
export async function calculatePaycheck(
  req: PaycheckRequest,
  signal?: AbortSignal,
): Promise<PaycheckResponse> {
  const url = `${baseUrl()}/api/payroll/paycheck`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
      response.status,
      url,
    );
  }

  return (await response.json()) as PaycheckResponse;
}

/**
 * POST /api/payroll/run — computes an entire roster's paychecks in ONE call,
 * replacing the old "one POST /api/payroll/paycheck per employee" shape (12 round
 * trips on every Payroll screen load — the visible "Calculating…" pause and the one
 * screen that flaked under parallel test load). A REQUEST-level 400 here means the
 * whole run was malformed (e.g. an empty roster); a per-employee validation failure
 * (negative gross, bad pay-period count, unconfigured tax year) instead comes back
 * as a 200 with that employee's row carrying `success: false` — see
 * PayrollRunEmployeeResult. Same "bypasses request(), no tenant header" reasoning as
 * calculatePaycheck above: this is a pure calculator, not RLS-scoped.
 */
export async function calculatePayrollRun(
  req: PayrollRunRequest,
  signal?: AbortSignal,
): Promise<PayrollRunResponse> {
  const url = `${baseUrl()}/api/payroll/run`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
      response.status,
      url,
    );
  }

  return (await response.json()) as PayrollRunResponse;
}

/**
 * POST /api/payroll/year-end — IRS Form 943 and each employee's W-2 boxes, projected
 * across the year from the roster's current rate.
 *
 * Same "bypasses request(), sends no tenant header" reasoning as calculatePaycheck and
 * calculatePayrollRun above: this is a pure calculator, not RLS-scoped. The roster it is
 * given was read from the session-gated GET /api/demo/employees by the caller.
 *
 * ⚠️ The response's `basis` is `"Projected"` — see YearEndForms. These are not filed
 * figures and not year-to-date; nothing persists a payroll run to total. A screen that
 * renders them without saying so is claiming something the data cannot support.
 *
 * A REQUEST-level 400 means the whole projection was malformed (empty roster, mixed tax
 * years, a projection length outside 1–366). A per-EMPLOYEE failure instead comes back as
 * a 200 with that employee listed in `excluded`, carrying its own reason — one bad row
 * never costs the rest of the roster its 943.
 */
export async function projectYearEndForms(
  req: YearEndRequest,
  signal?: AbortSignal,
): Promise<YearEndForms> {
  const url = `${baseUrl()}/api/payroll/year-end`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
      response.status,
      url,
    );
  }

  return (await response.json()) as YearEndForms;
}

/**
 * POST /api/payroll/quarterly — IRS Form 941 for one quarter, from a NON-agricultural
 * roster. Same "pure calculator, no tenant header" shape as its siblings above.
 *
 * ⚠️ Send the roster of a non-farm entity. The endpoint cannot tell one roster from
 * another, and a 941 built from agricultural wages is arithmetically valid and legally
 * wrong — those wages belong on the annual Form 943.
 */
export async function projectQuarterlyForm941(
  req: QuarterlyRequest,
  signal?: AbortSignal,
): Promise<QuarterlyForm> {
  const url = `${baseUrl()}/api/payroll/quarterly`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(`${response.status} ${response.statusText}${body ? `: ${body}` : ""}`, response.status, url);
  }

  return (await response.json()) as QuarterlyForm;
}

/**
 * POST /api/payroll/annual-futa — IRS Form 940, the annual federal unemployment return.
 *
 * Same "pure calculator, no tenant header" shape as its siblings above. Unlike the
 * 943/941 split this one applies to EVERY employer, so it is sent whichever business is
 * selected.
 */
export async function projectAnnualFuta(
  req: AnnualFutaRequest,
  signal?: AbortSignal,
): Promise<AnnualFutaForm> {
  const url = `${baseUrl()}/api/payroll/annual-futa`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(`${response.status} ${response.statusText}${body ? `: ${body}` : ""}`, response.status, url);
  }

  return (await response.json()) as AnnualFutaForm;
}

/**
 * POST /api/payroll/paid-leave — the MN Paid Leave engine (EasyFarm.PaidLeave):
 * per-check premiums, the run's totals, and the quarterly wage detail those checks roll
 * up to, in one call.
 *
 * Same "bypasses request(), sends no tenant header" reasoning as calculatePaycheck and
 * calculatePayrollRun above: this is a pure calculator, not RLS-scoped.
 *
 * A REQUEST-level 400 means the whole run was malformed — an empty roster, a state/year
 * the engine has no program for, or a formula-injection payload in the employer account
 * id. A per-LINE failure instead comes back as a 200 with that line carrying
 * `success: false` and its own `errors`, so one bad row never costs the rest of the
 * roster its result or its place on the wage detail.
 */
export async function calculatePaidLeave(
  req: PaidLeaveRequest,
  signal?: AbortSignal,
): Promise<PaidLeaveResponse> {
  const url = `${baseUrl()}/api/payroll/paid-leave`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
      signal,
    });
  } catch (cause) {
    throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
  }

  if (response.status === 400) {
    const problem = (await response.json().catch(() => null)) as ValidationProblem | null;
    throw new PayrollValidationError(problem?.errors ?? { request: ["Rejected — no detail returned."] });
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
      response.status,
      url,
    );
  }

  return (await response.json()) as PaidLeaveResponse;
}

/**
 * Raised by `authApi.login` for an invalid email/password. Deliberately carries no
 * detail beyond a generic message — the server returns the identical body for "no
 * such user" and "wrong password" (AuthEndpoints.cs), and this class must not invent
 * a distinction the server refuses to make.
 */
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

/**
 * Authentication — login/logout/"who am I" (feature/auth).
 *
 * Deliberately bypasses `request()`/`demoApi`: those require `VITE_DEMO_TENANT` and
 * always send `X-Demo-Tenant`, but none of these three routes read a tenant header —
 * they are what tenant identity comes FROM now (see DemoEndpoints.cs's
 * `TryOpenAuthenticatedSessionAsync`). Coupling auth to the demo-tenant env var would
 * be backwards.
 */
export const authApi = {
  /**
   * POST /api/auth/login. On success the server sets the session cookie via
   * `Set-Cookie` — this function never sees or stores a token itself, and none of the
   * response shape ever carries one (SessionUser is the entire body).
   */
  login: async (email: string, password: string, signal?: AbortSignal): Promise<SessionUser> => {
    const url = `${baseUrl()}/api/auth/login`;
    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        signal,
      });
    } catch (cause) {
      throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
    }

    if (response.status === 401) {
      // Same generic text regardless of which check failed server-side — never
      // "no such account" vs. "wrong password". See AuthError's own remarks.
      throw new AuthError("Incorrect email or password.");
    }
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }

    return (await response.json()) as SessionUser;
  },

  /** POST /api/auth/logout. Always succeeds (idempotent server-side, see AuthEndpoints.cs). */
  logout: async (signal?: AbortSignal): Promise<void> => {
    const url = `${baseUrl()}/api/auth/logout`;
    await fetch(url, { method: "POST", credentials: "same-origin", signal });
  },

  /**
   * GET /api/auth/me — "am I logged in, and as whom". Used once on app load to decide
   * whether to render the login form or the app shell (see useSession.ts). A 401 is
   * the expected, non-exceptional "not logged in" answer, so this resolves to `null`
   * rather than throwing for that one status.
   */
  me: async (signal?: AbortSignal): Promise<SessionUser | null> => {
    const url = `${baseUrl()}/api/auth/me`;
    let response: Response;
    try {
      response = await fetch(url, { credentials: "same-origin", signal });
    } catch (cause) {
      throw new ApiError(cause instanceof Error ? cause.message : "Network request failed", 0, url);
    }

    if (response.status === 401) return null;
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new ApiError(
        `${response.status} ${response.statusText}${body ? `: ${body}` : ""}`,
        response.status,
        url,
      );
    }

    return (await response.json()) as SessionUser;
  },

  /**
   * GET /api/auth/users — who can sign in to this farm.
   *
   * Owner-only server-side (403 for an employee login). This client does not check the
   * role and must not: a client-side role check is a rendering decision, and treating it
   * as the control is how the real one stops being maintained. Same reasoning as
   * usePendingTime's remarks.
   */
  listUsers: (signal?: AbortSignal): Promise<TenantUser[]> =>
    request<TenantUser[]>("/api/auth/users", { signal }),

  /**
   * POST /api/auth/users/{id}/revoke — switch a login off. Their live sessions stop
   * working on their very next request, not at the end of the 12h expiry.
   *
   * The server refuses two cases with 409 — revoking your own login, and revoking the
   * last active owner — and the message it returns says which. Those are surfaced to the
   * reader rather than swallowed: both mean "this specific thing would lock somebody
   * out", which is worth reading, not a generic failure.
   */
  revokeUser: (userId: string, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/auth/users/${encodeURIComponent(userId)}/revoke`, {
      method: "POST",
      signal,
    }),

  /** POST /api/auth/users/{id}/restore — put a login back. Coming back still requires
   *  signing in; restoring does not resurrect the sessions revoking destroyed. */
  restoreUser: (userId: string, signal?: AbortSignal): Promise<void> =>
    request<void>(`/api/auth/users/${encodeURIComponent(userId)}/restore`, {
      method: "POST",
      signal,
    }),
};
